import React, { useState, useEffect, useMemo, Fragment, useRef, createContext, useContext } from 'react';
import './home.css';
import { useInvestmentData, loopPositions, labelLoopSteps } from '../lib/useInvestmentData.js';
import { getSupabase } from '../lib/supabase.js';
import { PoolEntryActions } from './PoolEntryActions.jsx';
import { scheduleCopy, scheduleRows, equationShare } from '../lib/payoutSchedule.js';
import LogMovementModal from './LogMovementModal.jsx';
import PendingDrafts from './PendingDrafts.jsx';
import LogCostModal from '../components/LogCostModal.jsx';
import CostSuggestionsCard from '../components/CostSuggestionsCard.jsx';
import { settlementSub, settlementAmount } from '../lib/settlementRows.js';
import { sumInternalAccounts, netAfterSettling } from '../lib/taekusAccounts.js';

// Components below read the screen's data from this context so we don't
// have to prop-drill the giant data shape through every node. Home() wraps
// its render in <DataCtx.Provider value={data}>; helpers call useDATA().
const DataCtx = createContext(null);
const useDATA = () => useContext(DataCtx) || MOCK_DATA;

// Stub out the tweaks panel — designer-only tool, no production presence.
const TweaksPanel = ({ children }) => null;
const TweakSection = ({ children }) => null;
const TweakRadio = () => null;
const TweakSelect = () => null;
const TweakColor = () => null;
const TweakSlider = () => null;
const TweakText = () => null;
const TweakToggle = () => null;
const TweakButton = () => null;
const useTweaks = (defaults) => [defaults, () => {}];

// Default tweak values (since we removed the designer's tweaks panel, we
// just lock in the "production" defaults — subscribed app state, ring hero,
// no digest card, etc.)
const TWEAK_DEFAULTS = {
  appState: 'subscribed',
  heroStyle: 'Ring',
  density: 'Comfortable',
  loopOrientation: 'Horizontal',
  memberAvatars: true,
  settlementsDemo: 'populated',
  familyAdded: 'none',
  showDigest: false,
};

// Stubs for empty-state components (used in 'enrolled-no-loop' / 'enrolled-no-events'
// branches — not used in the default 'subscribed' state). Real ports of these
// live in _proto_empty-states.jsx and can be adapted later if needed.
const TeaserHero = () => null;
const HowItWorks = () => null;
const TermsCard = () => null;

// "How it works" modal — explains the partnership math to a new investor.
const HowItWorksModal = ({ onClose, navigate }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, zIndex: 9999,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'var(--surface, #fff)', color: 'var(--text-1, #0f172a)',
        borderRadius: 'var(--radius-lg, 16px)', padding: '28px 28px 20px',
        maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(15,23,42,0.3)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent, #4f46e5)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
            How the investment module works
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Hurdle → split, in 4 steps</h2>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 22, color: 'var(--text-3, #94a3b8)', lineHeight: 1,
            padding: 4, marginTop: -4,
          }}
        >✕</button>
      </div>

      <ol style={{ paddingLeft: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Step n="1" title="You subscribe to a loop">
          A loop is a named sequence of <strong>steps</strong> Kareem has authored — e.g. <span style={{ fontFamily: 'ui-monospace, monospace' }}>SoFi → Coinbase → BoA → SoFi</span>. Each step has a value rate (e.g. 2.2% on the SoFi→Coinbase leg). You also tell us how much each household member will move per cycle.
        </Step>
        <Step n="2" title="You actually move the money">
          As you move money between accounts (manually, or auto-logged from broker emails later), each movement is matched to a step. Movements that don't match a known step get queued for Kareem to set a rate.
        </Step>
        <Step n="3" title="Value events fire">
          For every matched movement, we compute <span style={{ fontFamily: 'ui-monospace, monospace' }}>amount × step.rate = value</span> and write an append-only value event. While your <strong>hurdle</strong> hasn't cleared, 100% of generated value goes to Kareem.
        </Step>
        <Step n="4" title="After the hurdle, you split 50/50">
          Once cumulative value to Kareem equals your hurdle (e.g. $10,000), every dollar of future value generated splits 50/50 between you and him. Settlements (Mercury, Zelle, etc.) pay down what you owe — they don't affect the hurdle.
        </Step>
      </ol>

      <div style={{ marginTop: 18, padding: '12px 14px', background: 'var(--accent-bg, #eef2ff)', borderRadius: 10, color: 'var(--accent-2, #3730a3)', fontSize: 13 }}>
        <strong>Your hurdle and split are set per-investor.</strong> Defaults are $10K / 50%; Kareem can adjust per partner (e.g. existing gold partners may have $0 hurdle).
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
        {/* T-017: no catalog CTA — loops are assigned by Kareem and activate
            automatically; there's nothing for the investor to browse. */}
        <button onClick={onClose} className="btn-primary" style={{ padding: '8px 16px' }}>Got it</button>
      </div>
    </div>
  </div>
);

const Step = ({ n, title, children }) => (
  <li style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 12, alignItems: 'flex-start' }}>
    <span style={{
      width: 28, height: 28, borderRadius: 14,
      background: 'var(--accent, #4f46e5)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 13, flexShrink: 0,
    }}>{n}</span>
    <div>
      <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-2, #475569)', lineHeight: 1.55 }}>{children}</div>
    </div>
  </li>
);
// Hero variant for enrolled-no-events: shows the hurdle but signals
// "waiting on first cycle" instead of progress numbers. Ported from
// _proto_empty-states.jsx but wired to real DATA.
const EmptyHurdleHero = () => {
  const DATA = useDATA();
  const hurdle = DATA.accountHolder?.hurdle || 0;
  const loopName = DATA.loop?.name || 'this loop';
  const firstStep = DATA.loop?.steps?.[0];
  const nextActionText = firstStep
    ? <>Execute Step 1 — <strong>{firstStep.source}</strong> → <strong>{firstStep.dest}</strong>{firstStep.rate ? <> @ {fmtPct(firstStep.rate)}</> : null}</>
    : <>Subscribe to a loop to start the hurdle clock.</>;
  return (
    <section className="hero hero-empty">
      <div className="hero-grid">
        <div className="hero-main">
          <div className="hero-eyebrow">
            <span className="dot dot-amber"></span>
            Subscribed · waiting on first cycle
          </div>
          <div className="hero-amount">
            <span className="hero-currency">$</span>
            <span className="hero-num">0</span>
            <span className="hero-of">/ ${hurdle.toLocaleString()}</span>
          </div>
          <div className="hero-sub">
            You're subscribed to <strong>{loopName}</strong>. The hurdle clock starts the moment
            the first step executes — no movements logged yet.
          </div>
          <div className="hurdle-bar">
            <div className="hurdle-bar-track">
              <div className="hurdle-bar-empty"></div>
            </div>
            <div className="hurdle-bar-labels">
              <span>0</span>
              <span style={{ color: '#fcd34d' }}>awaiting first event</span>
              <span>${hurdle.toLocaleString()}</span>
            </div>
          </div>
          <div className="hero-after">
            <div className="hero-after-row">
              <span className="after-step">
                <span className="after-num">→</span>
                <span className="after-text">Next action</span>
              </span>
              <span className="after-detail">{nextActionText}</span>
            </div>
          </div>
        </div>
        <div className="hero-stats">
          <HeroStat label="Outstanding to Kareem"  value="$0"  sub="nothing recovered yet" accent="empty" />
          <HeroStat label="Value generated · YTD"  value="$0"  sub="0 cycles complete" accent="empty" />
          <HeroStat label="Money cycled · YTD"     value="$0"  sub={'across ' + DATA.members.length + ' member' + (DATA.members.length === 1 ? '' : 's')} accent="empty" />
          <HeroStat label="Active loops"           value={String(DATA.activeLoopsCount ?? (DATA.loop ? 1 : 0))} sub={DATA.catalogCount + ' in catalog'} accent="ok" />
        </div>
      </div>
    </section>
  );
};

// Coral banner explaining the loop is configured but waiting for the
// first movement. Ported from _proto_empty-states.jsx.
const NoEventsLoopBanner = () => {
  const DATA = useDATA();
  const firstStep = DATA.loop?.steps?.[0];
  return (
    <div className="ne-loop-banner">
      <div className="nlb-icon">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
          <path d="M3 12a9 9 0 1 1 14.5 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          <path d="M21 7v5h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="nlb-body">
        <div className="nlb-title">Loop is armed — waiting on first execution</div>
        <div className="nlb-sub">
          {firstStep
            ? <>Step rates are set. Move money for <strong>{firstStep.source} → {firstStep.dest}</strong> to log Cycle 1.</>
            : <>Step rates are set. Click any step to see what's expected next.</>
          }
        </div>
      </div>
    </div>
  );
};

const NoEventsCard = () => null;
const TelegramDigestCard = () => null;



// ─── Mahmoud's family data (from INVESTMENT_MODULE_DESIGN.md) ───
// Kept as the read-only fallback for renders before the Supabase hook
// resolves and as documentation of the shape this screen renders. Real
// data comes from useInvestmentData() and flows through DataCtx below.
const MOCK_DATA = {
  accountHolder: {
    id: 'mahmoud',
    name: 'Mahmoud',
    initials: 'M',
    color: '#4f46e5',
    enrolledAt: '2026-05-13',
    hurdle: 10000,
    recovered: 1920,
    splitAfterHurdle: 0.5,
    settlementCadence: 'monthly',
  },
  members: [
    { id: 'mahmoud', name: 'Mahmoud', initials: 'M', role: 'holder', allocation: 5000, valueYTD: 640, moneyFlow: 20000, accounts: ['SoFi · savings', 'Coinbase · exchange', 'BoA · checking'], color: '#4f46e5' },
    { id: 'hala',    name: 'Hala',    initials: 'H', role: 'sub',    allocation: 5000, valueYTD: 480, moneyFlow: 15000, accounts: ['SoFi · savings', 'Coinbase · exchange', 'BoA · checking'], color: '#0ea5e9' },
    { id: 'yusuf',   name: 'Yusuf',   initials: 'Y', role: 'sub',    allocation: 3000, valueYTD: 480, moneyFlow: 9000,  accounts: ['SoFi · savings', 'Coinbase · exchange', 'BoA · checking'], color: '#f59e0b' },
    { id: 'layla',   name: 'Layla',   initials: 'L', role: 'sub',    allocation: 3000, valueYTD: 320, moneyFlow: 9000,  accounts: ['SoFi · savings', 'Coinbase · exchange', 'BoA · checking'], color: '#ec4899' },
  ],
  loop: {
    id: 'crypto-yield',
    name: 'Crypto Yield Cycle',
    description: 'A 3-step rotation: stable savings → crypto exchange → checking → back to savings.',
    valueYTD: 1920,
    cyclesCompleted: 4,
    cyclesInProgress: 0,
    steps: [
      { idx: 1, source: 'SoFi',     sourceKind: 'savings_hy',     dest: 'Coinbase', destKind: 'crypto_exchange', rate: 0.022, movesYTD: 16, valueYTD: 1408, executed: { mahmoud: 4, hala: 4, yusuf: 4, layla: 4 } },
      { idx: 2, source: 'Coinbase', sourceKind: 'crypto_exchange', dest: 'BoA',     destKind: 'checking',         rate: 0.000, movesYTD: 14, valueYTD: 0,    executed: { mahmoud: 4, hala: 4, yusuf: 3, layla: 3 } },
      { idx: 3, source: 'BoA',      sourceKind: 'checking',        dest: 'SoFi',    destKind: 'savings_hy',       rate: 0.010, movesYTD: 12, valueYTD: 512,  executed: { mahmoud: 4, hala: 4, yusuf: 2, layla: 2 } },
    ],
  },
  settlements: {
    outstanding: 1920,
    lifetimeSettled: 0,
    pendingTotal: 0,
    cadence: 'monthly',
    nextNudge: 'Jun 13',
    recent: [], // populated in tweak demo state
    demoRecent: [
      { id: 'SETL-2026-0514-A4F2', dateLabel: 'May 14', amount: 1920, method: 'mercury', methodName: 'Mercury', status: 'pending', memo: 'Cycle 4 settle' },
      { id: 'SETL-2026-0415-7B3C', dateLabel: 'Apr 15', amount: 580,  method: 'zelle',   methodName: 'Zelle',   status: 'confirmed', memo: 'Monthly · cycle 3' },
      { id: 'SETL-2026-0315-2E1A', dateLabel: 'Mar 15', amount: 420,  method: 'coinbase',methodName: 'Coinbase',status: 'confirmed', memo: '' },
    ],
  },
  recentEvents: [
    { date: 'May 12', member: 'hala',    flow: 'SoFi → Coinbase', amount: 5000, rate: 0.022, value: 110, kareem: 110, investor: 0 },
    { date: 'May 12', member: 'mahmoud', flow: 'SoFi → Coinbase', amount: 5000, rate: 0.022, value: 110, kareem: 110, investor: 0 },
    { date: 'May 11', member: 'mahmoud', flow: 'BoA → SoFi',      amount: 3000, rate: 0.010, value: 30,  kareem: 30,  investor: 0 },
    { date: 'May 11', member: 'hala',    flow: 'BoA → SoFi',      amount: 3000, rate: 0.010, value: 30,  kareem: 30,  investor: 0 },
    { date: 'May 10', member: 'yusuf',   flow: 'Coinbase → BoA',  amount: 3000, rate: 0.000, value: 0,   kareem: 0,   investor: 0 },
    { date: 'May 09', member: 'yusuf',   flow: 'SoFi → Coinbase', amount: 3000, rate: 0.022, value: 66,  kareem: 66,  investor: 0 },
    { date: 'May 09', member: 'layla',   flow: 'SoFi → Coinbase', amount: 3000, rate: 0.022, value: 66,  kareem: 66,  investor: 0 },
  ],
  nudges: [
    { kind: 'pending', text: 'Yusuf & Layla pending Step 2 — Coinbase → BoA', detail: 'Move $3K each to advance Cycle 5', cta: 'Confirm sweep' },
    { kind: 'catalog', text: 'New step active: BoA → Wealthfront @ 1%', detail: 'Added to "Stable Yield" loop.', cta: 'View loop' },
  ],
  catalogCount: 7,
};

// ─── Helpers ───
// T-039c: exact-cents formatter for the who-owes-whom equations — the terms
// must visibly sum to the result, so they render unrounded.
const fmtC = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt$ = (n, d = 0) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Math.abs(n);
  return (n < 0 ? '-' : '') + '$' + v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmt$Big = (n) => {
  if (n >= 1000) return '$' + (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + 'K';
  return '$' + n.toLocaleString();
};
// Display a decimal rate (0.0325) as a percent string ("3.25%"), preserving
// entered precision up to 3 decimal places and stripping trailing zeros so
// nice round values still read as "2%" / "3.2%" without ".00" padding.
const fmtPct = (n, maxDecimals = 3) => {
  const v = Number(n || 0) * 100;
  return parseFloat(v.toFixed(maxDecimals)).toString() + '%';
};

// Step-duration label — "Instant" italic when 0 (the step fires the moment
// the source movement settles); "N biz" / "N cal" otherwise. Matches the
// admin Catalog's fmtStepDurationStep().
const fmtStepDays = (days, isBusiness) => {
  const d = Number(days || 0);
  if (!d) return { text: 'Instant', italic: true };
  return { text: d + ' ' + (isBusiness ? 'biz' : 'cal') + ' day' + (d === 1 ? '' : 's'), italic: false };
};
// Wait label — "—" when 0 (no required wait, just step → step), "N biz/cal" otherwise.
const fmtWaitDays = (days, isBusiness) => {
  const d = Number(days || 0);
  if (!d) return { text: '—', muted: true };
  return { text: d + ' ' + (isBusiness ? 'biz' : 'cal') + ' day' + (d === 1 ? '' : 's'), muted: false };
};
// Per-step amount — "Max" italic when null (move whatever's available),
// otherwise the dollar amount in compact form.
const fmtStepAmount = (amt) => {
  if (amt == null) return { text: 'Max', italic: true };
  return { text: '$' + Math.round(amt).toLocaleString(), italic: false };
};

// Parse an event date (YYYY-MM-DD or ISO timestamp) as local midnight so
// "days until X" math is timezone-stable: comparing two values that were
// both anchored to local midnight always gives clean whole-day diffs.
function parseEventDate(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function todayLocalMidnight() {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

// Compute the next-movement info for a loop. Looks at the loop's most-
// recent event (lastEventAt / lastEventStepIdx from useInvestmentData)
// and adds (step_duration + wait_duration) of that step — biz days
// converted to cal days at 5:7 — to derive when the NEXT step is due.
//
// Returns:
//   {
//     step:          the next step object (steps wrap: step N+1 → step 1),
//     lastDate:      ISO date of the last event (or null),
//     dueDate:       ISO date when the next movement is due (or null),
//     daysRemaining: integer days until due, negative if overdue,
//     status:        'awaiting-kickoff' | 'ready' | 'overdue' | 'due-today' | 'scheduled',
//   }
//
// Returns null when the loop has no steps at all.
function computeNextStep(loop) {
  if (!loop || !Array.isArray(loop.steps) || loop.steps.length === 0) return null;
  const steps = loop.steps;
  // No events yet → cycle 1 is "start anytime", no countdown.
  if (!loop.lastEventAt || loop.lastEventStepIdx == null) {
    return { step: steps[0], lastDate: null, dueDate: null, daysRemaining: null, status: 'awaiting-kickoff' };
  }
  const lastIdx = steps.findIndex((s) => s.idx === loop.lastEventStepIdx);
  const safeLastIdx = lastIdx >= 0 ? lastIdx : 0;
  const lastStep = steps[safeLastIdx];
  // Next step wraps: step N → step 1 of next cycle.
  const nextStep = steps[(safeLastIdx + 1) % steps.length];
  const sd = Number(lastStep.stepDurationDays || 0);
  const wd = Number(lastStep.waitDurationDays || 0);
  const stepCal = lastStep.stepDurationIsBusiness ? sd * (7 / 5) : sd;
  const waitCal = lastStep.waitDurationIsBusiness ? wd * (7 / 5) : wd;
  const totalDays = stepCal + waitCal;
  const last = parseEventDate(loop.lastEventAt);
  const today = todayLocalMidnight();
  const dueMs = (last ? last.getTime() : today.getTime()) + totalDays * 24 * 60 * 60 * 1000;
  const due = new Date(dueMs);
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysRemaining = Math.ceil((dueMs - today.getTime()) / msPerDay);
  // Prefer the shared brain's authoritative due-date (investor_next_steps:
  // wait_duration off the MOVEMENT date, weekend/holiday-aware) so this card
  // matches the agent's reminders exactly. Fall back to the local estimate
  // (step+wait off the event date) only when the brain has no row yet.
  let outDue = due, outDays = daysRemaining, outStep = nextStep, usingBrain = false;
  if (loop.brainDueDate) {
    const bDue = new Date(loop.brainDueDate + 'T00:00:00');
    if (!isNaN(bDue.getTime())) {
      usingBrain = true;
      outDue = bDue;
      outDays = (loop.brainDaysRemaining != null)
        ? Number(loop.brainDaysRemaining)
        : Math.ceil((bDue.getTime() - today.getTime()) / msPerDay);
    }
    if (loop.brainNextStepOrder != null) {
      const bs = steps.find((s) => s.idx === loop.brainNextStepOrder);
      if (bs) outStep = bs;
    }
  }
  let status;
  if (totalDays === 0 && !usingBrain) status = 'ready';
  else if (outDays < 0) status = 'overdue';
  else if (outDays === 0) status = 'due-today';
  else status = 'scheduled';
  return {
    step: outStep,
    lastDate: loop.lastEventAt,
    dueDate: outDue.toISOString().slice(0, 10),
    daysRemaining: outDays,
    status,
  };
}

// Short human label for a next-step status. Used by both the per-loop
// countdown pill and the aggregator row so the wording stays consistent.
function nextStepLabel(info) {
  if (!info) return '';
  switch (info.status) {
    case 'awaiting-kickoff': return 'Start anytime';
    case 'ready':            return 'Ready now';
    case 'due-today':        return 'Due today';
    case 'overdue': {
      const n = Math.abs(info.daysRemaining);
      return `Overdue by ${n} day${n === 1 ? '' : 's'}`;
    }
    case 'scheduled': {
      const n = info.daysRemaining;
      return `in ${n} day${n === 1 ? '' : 's'}`;
    }
    default: return '';
  }
}

// ─── Admin banner (top-of-page strip shown to admin viewers) ───
// Token-based styling so it inherits Inter + the design palette.
const AdminBanner = ({ onOpen, text, block = false }) => (
  <div
    className="admin-banner"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: block ? '10px 14px' : '10px 16px',
      marginBottom: block ? 16 : 0,
      background: 'var(--accent-bg)',
      borderBottom: block ? 'none' : '1px solid var(--accent-soft)',
      border: block ? '1px solid var(--accent-soft)' : 'none',
      borderRadius: block ? 'var(--radius)' : 0,
      color: 'var(--accent-2)',
      fontSize: 13,
      fontFamily: 'inherit',
    }}
  >
    <span style={{
      fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
      padding: '3px 8px', borderRadius: 999,
      background: 'var(--accent)', color: '#fff',
    }}>Admin</span>
    <span style={{ opacity: 0.9 }}>{text}</span>
    <button
      type="button"
      onClick={onOpen}
      className="btn-ghost-pill"
      style={{ marginLeft: 'auto', textDecoration: 'none' }}
    >
      Open catalog admin →
    </button>
  </div>
);

// ─── Avatar ───
const Avatar = ({ member, size = 36, ring = false }) => (
  <div className="avatar" style={{
    width: size, height: size,
    background: `linear-gradient(135deg, ${member.color}, ${shade(member.color, -20)})`,
    fontSize: size * 0.42,
    boxShadow: ring ? `0 0 0 2px #fff, 0 0 0 4px ${member.color}40` : 'none',
  }}>
    {member.initials}
  </div>
);
function shade(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + (R < 255 ? R < 0 ? 0 : R : 255) * 0x10000 + (G < 255 ? G < 0 ? 0 : G : 255) * 0x100 + (B < 255 ? B < 0 ? 0 : B : 255)).toString(16).slice(1);
}

// ─── App Chrome (matches existing FinOps look) ───


// ─── Hero: Hurdle Gauge — bar or ring depending on tweaks.heroStyle ───
// ── T-009: pool quick-decision cards (hero) ─────────────────────────────
// Two numbers side by side so an investor can size a draw at a glance: what
// the POOL holds right now (taekus_pool_snapshot, 2-min refresh) and what
// THEY hold in it (v2_pool_position household available — the same source as
// the Balances pool card). Personal balance renders green when positive, red
// when negative (owner spec 2026-08-07). These cards replaced the standalone
// PoolFundingStrip the same day; colors are the hero's on-dark palette.

// Owner directive 2026-08-07: what investors see as "Money available in the
// pool" is Taekus's available MINUS this fixed protection buffer, floored at
// zero — a deliberate under-statement so draw decisions always leave
// headroom. The snapshot table keeps the raw truth; the buffer is purely a
// display rule.
const POOL_FUNDING_BUFFER = 15000;

// Owner reframe 2026-08-07 (closes the "−4,000 attribution" question): the
// pool account is Kareem's, so his own household "position" is not a debt —
// the number that matters to him is the ownership split. Admin hero (not
// act-as) therefore shows four cards: total in the pool (raw Taekus), what
// other investors own (Σ positive positions, his household excluded), what
// he owns (pool − others' ownership), and what others owe (Σ negative
// positions). Investors and act-as previews keep the buffered pair.
//
// Kareem's admin login has NO linked member (his Home is AdminOverview, the
// platform view), so the household to exclude from investor math cannot be
// derived from a member prop there — it is pinned, same spirit as the gold
// bridge's kareem-only rule.
//
// Kareem 2026-10-23: two more admin cards that span EVERY Taekus account he
// holds, not just the TK-C pool — "Total in Taekus" (every internal account
// summed, minus any type CREDIT account whose "available" is a credit line
// — see isCreditAccount) and "Yours after everyone settles" (that total − others' ownership
// + what others owe: if every creditor withdrew and every debtor paid, what
// stays). Read live through the operator-only taekus-read proxy rather than
// the shared snapshot row, because the other accounts' balances are not for
// investors' eyes and the proxy already refuses non-operators.
export const POOL_OWNER_HOUSEHOLD = 'kareem';

// One authenticated GET through the read-only Taekus proxy (the same shape
// CatalogAdmin's probes use). Resolves to the parsed body or throws with
// the proxy's message.
async function readTaekusAccounts(sb) {
  const { data: sess } = await sb.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error('No Supabase session.');
  const qs = new URLSearchParams({ path: '/api/banking/internal-accounts/' });
  const res = await fetch('/.netlify/functions/taekus-read?' + qs.toString(),
    { headers: { Authorization: 'Bearer ' + token } });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error || ('Taekus proxy returned HTTP ' + res.status));
  return body;
}

export function PoolHeroCards({ member, adminView = false }) {
  const [snap, setSnap] = useState(null);
  const [positions, setPositions] = useState(null);   // every row the RPC returns for this caller
  // Admin only: every Taekus account summed ({ total, count, skipped,
  // accounts }) or { error } when the proxy refused / Taekus was down.
  const [allAccounts, setAllAccounts] = useState(null);
  useEffect(() => {
    const sb = getSupabase();
    if (!sb || (!member && !adminView)) return undefined;
    let cancelled = false;
    sb.from('taekus_pool_snapshot').select('*').eq('id', 1).maybeSingle()
      .then(({ data }) => { if (!cancelled && data) setSnap(data); });
    sb.rpc('v2_pool_position').then(({ data: rows, error }) => {
      if (cancelled || error || !Array.isArray(rows)) return;
      setPositions(rows);
    }, () => {});
    if (adminView) {
      readTaekusAccounts(sb).then(
        (body) => { if (!cancelled) setAllAccounts(sumInternalAccounts(body)); },
        (e) => { if (!cancelled) setAllAccounts({ error: e?.message || 'Taekus read failed' }); });
    }
    return () => { cancelled = true; };
  }, [member?.id, adminView]);
  if (!snap && positions == null) return null;
  const money = (n) => '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ageMin = snap ? Math.max(0, Math.round((Date.now() - new Date(snap.fetched_at).getTime()) / 60000)) : null;
  const stale = ageMin != null && ageMin > 10;   // five missed 2-minute refresh cycles
  const asOf = snap == null ? null
    : ageMin < 1 ? 'just now'
    : ageMin < 60 ? ageMin + ' min ago'
    : new Date(snap.fetched_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const hh = member ? (member.managed_by_member_id || member.id) : POOL_OWNER_HOUSEHOLD;
  const asOfSub = snap ? (stale ? 'stale — as of ' + asOf : 'as of ' + asOf) : 'no snapshot yet';
  const card = (label, value, sub, valueStyle, subStyle) => (
    <div className="hero-stat" style={{ flex: 1, minWidth: 150 }}>
      <div className="hs-label">{label}</div>
      <div className="hs-value" style={valueStyle}>{value}</div>
      <div className="hs-sub" style={subStyle}>{sub}</div>
    </div>
  );

  if (adminView) {
    const others = (positions || []).filter((r) => r.household !== hh);
    const othersOwn = others.reduce((s, r) => s + Math.max(0, Number(r.available) || 0), 0);
    const othersOwe = others.reduce((s, r) => s + Math.max(0, -(Number(r.available) || 0)), 0);
    const ownersCount = others.filter((r) => Number(r.available) > 0).length;
    const owersCount = others.filter((r) => Number(r.available) < 0).length;
    const mineShare = snap && positions ? Number(snap.available) - othersOwn : null;
    // All-Taekus pair: the total needs only the live read; the net also
    // needs the position rows. A failed read says why instead of a dash
    // that could be mistaken for "nothing there".
    const acctsOk = allAccounts && !allAccounts.error;
    const totalAll = acctsOk ? allAccounts.total : null;
    const acctsSub = allAccounts == null ? 'reading Taekus…'
      : allAccounts.error ? allAccounts.error
      : 'live · ' + allAccounts.count + ' account' + (allAccounts.count === 1 ? '' : 's')
        + (allAccounts.accounts.length
          ? ' · ' + allAccounts.accounts.map((a) => (a.name || (a.last4 ? '·' + a.last4 : 'account')) + ' ' + money(a.available)).join(' · ')
          : '')
        + (allAccounts.excluded ? ' · ' + allAccounts.excluded + ' credit line left out' : '')
        + (allAccounts.skipped ? ' · ' + allAccounts.skipped + ' unreadable' : '');
    const netMine = totalAll != null && positions ? netAfterSettling({ totalAll, othersOwn, othersOwe }) : null;
    return (
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {card('Total money in the pool', snap ? money(snap.available) : '—', asOfSub,
          undefined, stale ? { color: '#fda4af' } : undefined)}
        {card('Other investors own', positions ? money(othersOwn) : '—',
          positions ? ownersCount + ' household' + (ownersCount === 1 ? '' : 's') + ' in the black' : 'position data unavailable')}
        {card('You own', mineShare != null ? money(mineShare) : '—',
          'pool minus others’ ownership',
          mineShare != null ? { color: mineShare > 0 ? '#6ee7b7' : mineShare < 0 ? '#fda4af' : undefined } : undefined)}
        {card('Others owe the pool', positions ? money(othersOwe) : '—',
          positions ? (owersCount ? owersCount + ' household' + (owersCount === 1 ? '' : 's') + ' drawn negative' : 'nobody in the red') : 'position data unavailable',
          positions && othersOwe > 0 ? { color: '#fda4af' } : undefined)}
        {card('Total in Taekus', totalAll != null ? money(totalAll) : '—', acctsSub,
          undefined, allAccounts?.error ? { color: '#fda4af' } : undefined)}
        {card('Yours after everyone settles', netMine != null ? money(netMine) : '—',
          totalAll != null && !positions ? 'position data unavailable' : 'all Taekus − others own + others owe',
          netMine != null ? { color: netMine > 0 ? '#6ee7b7' : netMine < 0 ? '#fda4af' : undefined } : undefined)}
      </div>
    );
  }

  const mineRow = positions ? (positions.find((r) => r.household === hh) || { available: 0 }) : null;
  const my = Number(mineRow?.available ?? 0);
  const myColor = mineRow == null ? undefined : my > 0 ? '#6ee7b7' : my < 0 ? '#fda4af' : undefined;
  const buffered = snap ? Math.max(0, Number(snap.available) - POOL_FUNDING_BUFFER) : null;
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
      {card('Money available in the pool', snap ? money(buffered) : '—', asOfSub,
        undefined, stale ? { color: '#fda4af' } : undefined)}
      {card('Your pool balance', mineRow == null ? '—' : money(my), 'household position',
        myColor ? { color: myColor } : undefined)}
    </div>
  );
}

const HurdleHero = ({ tweaks, member, adminView = false }) => {
  const DATA = useDATA();
  const { hurdle, recovered } = DATA.accountHolder;
  const remaining = Math.max(0, hurdle - recovered);
  const pct = hurdle > 0 ? Math.min(1, recovered / hurdle) : 0;
  const valueYTD = DATA.householdValueYTD ?? (DATA.loop?.valueYTD || 0);
  const cycled = DATA.members.reduce((s, m) => s + (m.moneyFlow || 0), 0);
  const outstanding = DATA.settlements?.outstanding ?? recovered;
  const credit = DATA.settlements?.credit || 0;
  const poolDeficit = DATA.settlements?.profitPool != null && DATA.settlements.profitPool < 0
    ? Math.round(Math.abs(DATA.settlements.profitPool)) : 0;
  // T-039c (owner spec): the hero tiles ARE the waterfall, in friendly terms.
  // W = rounded display figures; R = unrounded terms so the equation
  // reconciles to the cent (rounded components don't sum).
  const W = DATA.settlements || {};
  const R = W.raw || W;
  const youHold = Math.round(Number(R.totalValue || 0) - Number(R.poolHeld || 0));
  const isRing = tweaks && tweaks.heroStyle === 'Ring';
  // Past the hurdle the investor keeps their side of the split; the
  // equation needs that term to sum (it printed rewards − costs − Kareem
  // holds = −credit, which only holds below the hurdle).
  const share = equationShare({
    investorNet: R.investorNet, rewards: R.totalValue, costs: R.claimCosts ?? R.recognizedCosts,
    held: R.poolHeld, paidIn: R.paid, paidOut: R.paidOut, waived: R.waivers,
    result: credit > 0 ? -Number(R.credit ?? credit) : Number(R.outstanding ?? outstanding),
  });
  const shareTerm = share > 0 ? <> − your share {fmtC(share)}</> : null;
  // T-055 (Beko, 2026-10-20): the equation's cost leg is the investor-paid
  // slice (D-13), while the "Total costs" tile counts everything, Kareem-paid
  // included. Two different "costs" figures on one hero read as a mistake
  // (his showed costs $0.00 beside Total costs $483), so the leg names what
  // it is whenever the two differ, and the tile says both things it knows.
  const kareemPaid = Number(W.kareemPaidCosts || 0);
  const costsLabel = kareemPaid > 0 ? 'costs you paid' : 'costs';
  const totalCostsSub = [
    kareemPaid > 0 ? <React.Fragment key="k">incl. {fmt$(kareemPaid)} Kareem paid — slows his clock, never your bill</React.Fragment> : null,
    W.costsProvisional ? <React.Fragment key="p">some months still confirming</React.Fragment> : null,
  ].filter(Boolean);
  // Owner, 2026-10-19: each month's balance is paid by the end of the next.
  const payout = credit > 0 ? scheduleCopy(W.schedule, fmt$) : null;
  // All-time net: the investor's side of the split.
  const investorNet = Number(W.investorNet || 0);
  const myPct = Math.round((1 - Number(DATA.accountHolder.splitAfterHurdle ?? 0.5)) * 100);
  const netSub = investorNet > 0
    ? (hurdle > 0 ? `your ${myPct}% of profit past the ${fmt$(hurdle)} hurdle` : `your ${myPct}% of profit · all time`)
    : hurdle > 0
      ? `your ${myPct}% starts once the ${fmt$(hurdle)} hurdle clears · ${Math.floor(pct * 100)}% there`
      : 'all time';

  // Derived pace + cycles-to-clearance. Falls back gracefully when there
  // aren't enough completed cycles to project a pace, or when the hurdle
  // is already cleared.
  const cyclesCompleted = DATA.loop?.cyclesCompleted || 0;
  const avgValuePerCycle = cyclesCompleted > 0 ? valueYTD / cyclesCompleted : 0;
  const cyclesToClearance = avgValuePerCycle > 0 ? Math.ceil(remaining / avgValuePerCycle) : null;
  const paceLabel = avgValuePerCycle > 0 ? fmt$(Math.round(avgValuePerCycle)) : null;

  // T-043: the allocation-driven household economics (totalAllocated /
  // effectiveAPY) are gone with the allocations concept — the hero strip
  // that displayed them was already retired by T-039c, and money math has
  // always used actuals, never allocations.

  return (
    <section className={'hero' + (isRing ? ' hero-ring' : '')}>
      <PoolHeroCards member={member} adminView={adminView} />
      <div className="hero-grid">
        <div className="hero-main">
          {isRing ? (
            <div className="ring-layout">
              <HurdleRing pct={pct} recovered={recovered} hurdle={hurdle} />
              <div className="ring-copy">
                <div className="hero-eyebrow">
                  <span className="dot"></span>
                  Hurdle progress · {DATA.accountHolder.name}'s ledger
                </div>
                <h2 className="ring-headline">
                  <span className="ring-headline-amt">${remaining.toLocaleString()}</span> to clearance
                </h2>
                {/* Only show this paragraph when there's actual dynamic
                    content to convey — either the hurdle has cleared, or
                    we have enough completed cycles to project pace.
                    Pre-first-cycle state hides it entirely; the static
                    "100% → Kareem" rule lives in product docs, not in the
                    middle of every render. */}
                {remaining === 0 ? (
                  <p className="ring-sub">Hurdle cleared. Every future dollar splits 50/50.</p>
                ) : cyclesToClearance != null ? (
                  <p className="ring-sub">At ~{paceLabel}/cycle, <strong>~{cyclesToClearance} cycle{cyclesToClearance === 1 ? '' : 's'}</strong> to the 50/50 split.</p>
                ) : null}
                {/* T-039c: allocated/APY strip retired from the hero (owner
                    spec) — the projection math lives on the loop panels. */}
              </div>
            </div>
          ) : (
            <>
              <div className="hero-eyebrow">
                <span className="dot"></span>
                Hurdle progress · {DATA.accountHolder.name}'s ledger with Kareem
              </div>
              <div className="hero-amount">
                <span className="hero-currency">$</span>
                <span className="hero-num">{recovered.toLocaleString()}</span>
                <span className="hero-of">/ ${hurdle.toLocaleString()}</span>
              </div>
              {/* Only show this paragraph when there's actual dynamic
                  info to convey (hurdle cleared, or pace data available).
                  Pre-first-cycle the bar + hero-amount already shows the
                  remaining $ visually — no need for a paragraph that just
                  restates the static rule. */}
              {remaining === 0 ? (
                <div className="hero-sub">Hurdle cleared. Every future dollar splits 50/50.</div>
              ) : cyclesToClearance != null ? (
                <div className="hero-sub">
                  <strong>${remaining.toLocaleString()}</strong> remaining. At your current pace (~{paceLabel}/cycle), ~<strong>{cyclesToClearance} cycle{cyclesToClearance === 1 ? '' : 's'}</strong> to the 50/50 split.
                </div>
              ) : null}

              <div className="hurdle-bar">
                <div className="hurdle-bar-track">
                  <div className="hurdle-bar-fill" style={{ width: (pct * 100) + '%' }}>
                    <div className="hurdle-bar-shine"></div>
                  </div>
                  <div className="hurdle-bar-tick" style={{ left: '50%' }}><span>50%</span></div>
                </div>
                <div className="hurdle-bar-labels">
                  <span>0</span>
                  <span className="hurdle-bar-current" style={{ left: `calc(${pct * 100}% - 1px)` }}>
                    <span className="caret"></span>
                    {fmtPct(pct)}
                  </span>
                  <span>${hurdle.toLocaleString()}</span>
                </div>
              </div>

              {/* T-039c: allocated/APY strip retired from the hero (owner
                  spec) — the projection math lives on the loop panels. */}
            </>
          )}
        </div>

        <div className="hero-stats">
          {/* T-039c (owner spec): the tiles ARE the math, in friendly terms —
              total rewards − total costs − what Kareem already holds − paid −
              waived = who owes whom. The who-owes-whom tile is the full-width
              headline and pops green when the direction reverses. Active
              loops / money cycled retired from the hero (Release 5 T-022
              contract feed unchanged underneath). Equation renders exact
              cents (R) so the terms visibly sum; tiles show rounded (W). */}
          {/* D-9: R.paid is what YOU paid in; R.paidOut is what Kareem paid
              back — both terms render so the equation keeps summing after a
              payout (the old net term went negative and vanished). */}
          {credit > 0 ? (
            <HeroStat label="Kareem owes you" value={fmt$(credit)} accent="credit hs-wide"
              sub={<>rewards {fmtC(R.totalValue)} − {costsLabel} {fmtC(R.claimCosts ?? R.recognizedCosts)}{shareTerm} − Kareem holds {fmtC(R.poolHeld)}{Number(R.paid || 0) > 0 ? <> − paid {fmtC(R.paid)}</> : null}{Number(R.paidOut || 0) > 0 ? <> + paid back {fmtC(R.paidOut)}</> : null}{Number(R.waivers || 0) > 0 ? <> − waived {fmtC(R.waivers)}</> : null} = −{fmtC(R.credit ?? credit)}</>} />
          ) : (
            <HeroStat label="Outstanding to Kareem" value={fmt$(outstanding)} accent="warn hs-wide"
              sub={outstanding === 0 && poolDeficit > 0
                ? `nothing due yet — $${poolDeficit.toLocaleString()} deficit carried forward`
                : <>rewards {fmtC(R.totalValue)} − {costsLabel} {fmtC(R.claimCosts ?? R.recognizedCosts)}{shareTerm} − Kareem holds {fmtC(R.poolHeld)}{Number(R.paid || 0) > 0 ? <> − paid {fmtC(R.paid)}</> : null}{Number(R.paidOut || 0) > 0 ? <> + paid back {fmtC(R.paidOut)}</> : null}{Number(R.waivers || 0) > 0 ? <> − waived {fmtC(R.waivers)}</> : null} = {fmtC(R.outstanding ?? outstanding)}</>} />
          )}
          {/* Owner, 2026-10-19: what Kareem owes is not all payable now —
              each month's balance is paid by the end of the next month, so
              the schedule gets its own line. Beside it, the investor's net
              earnings to date. With nothing scheduled the net tile takes the
              row. */}
          {payout && (
            <HeroStat label={payout.label} value={fmt$(payout.amount)} sub={payout.sub}
              accent={payout.overdue ? 'warn' : 'ok'} />
          )}
          <HeroStat label="Net earned · all time" value={fmt$(investorNet)} sub={netSub}
            accent={(investorNet > 0 ? 'ok' : 'neutral') + (payout ? '' : ' hs-wide')} />
          <HeroStat label="Total rewards" value={fmt$(W.totalValue)} sub={(DATA.householdCyclesCompleted ?? DATA.loop.cyclesCompleted) + ' cycles complete · all-time'} accent="ok" />
          <HeroStat label="Total costs"   value={fmt$(W.recognizedCosts)} sub={totalCostsSub.length ? totalCostsSub.reduce((acc, x, i) => (i ? [...acc, ' · ', x] : [x]), []) : 'operating costs to date'} accent="neutral" />
          <HeroStat label="You hold"      value={fmt$(youHold)} sub="rewards sitting on your own cards" accent="neutral" />
          <HeroStat label="Kareem holds"  value={fmt$(W.poolHeld)} sub="rewards already on the pool card" accent="neutral" />
        </div>
      </div>
    </section>
  );
};

const HeroStat = ({ label, value, sub, accent }) => (
  <div className={'hero-stat hs-' + accent}>
    <div className="hs-label">{label}</div>
    <div className="hs-value">{value}</div>
    <div className="hs-sub">{sub}</div>
  </div>
);

// ─── Hurdle ring gauge — radial progress SVG used when heroStyle === 'Ring' ───
const HurdleRing = ({ pct, recovered, hurdle }) => {
  const size = 220;
  const stroke = 20;
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const offset = C * (1 - pct);
  const pctLabel = (pct * 100).toFixed(1).replace(/\.0$/, '');
  return (
    <div className="hurdle-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <linearGradient id="hurdleRingGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#a7f3d0" />
          </linearGradient>
          <filter id="hurdleRingGlow">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        <circle cx={size/2} cy={size/2} r={r}
          stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
        <circle cx={size/2} cy={size/2} r={r}
          stroke="url(#hurdleRingGrad)" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset}
          filter="url(#hurdleRingGlow)" opacity="0.6" />
        <circle cx={size/2} cy={size/2} r={r}
          stroke="url(#hurdleRingGrad)" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset .8s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />
        <circle cx={size/2} cy={size/2} r={r * 0.78}
          stroke="rgba(255,255,255,0.04)" strokeWidth={1} fill="none" strokeDasharray="2 6" />
      </svg>
      <div className="hurdle-ring-center">
        <div className="hrc-label">Cleared</div>
        <div className="hrc-pct mono">
          {pctLabel}<span className="hrc-pct-sign">%</span>
        </div>
        <div className="hrc-amt mono">
          ${recovered.toLocaleString()} <span className="hrc-amt-of">/ ${hurdle.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

// Prominent next-action strip on each LoopEngine card. Sits between the
// header and the step chain so it reads as the card's primary call-to-
// action ("what should I do on this loop right now?") instead of a
// quiet pill tucked into the title block.
//
// Visually picks up the urgency tokens shared with the Next Steps
// aggregator (overdue=rose, due-today/ready=amber, awaiting-kickoff
// dashed) but with full-width treatment and a stronger countdown pill.
// Hidden entirely when the loop has no steps.
const LoopNextStepBanner = ({ loop }) => {
  const info = computeNextStep(loop);
  if (!info || !info.step) return null;
  return (
    <div className={'loop-next-banner loop-next-banner--' + info.status} role="status">
      <div className="loop-next-banner-icon" aria-hidden="true">
        {info.status === 'overdue' ? '!'
          : info.status === 'due-today' || info.status === 'ready' ? '◆'
          : info.status === 'awaiting-kickoff' ? '○'
          : '→'}
      </div>
      <div className="loop-next-banner-body">
        <div className="loop-next-banner-lbl">Next step</div>
        <div className="loop-next-banner-step">
          <span className="loop-next-banner-num">Step {info.step.label || info.step.idx}</span>
          <span className="loop-next-banner-flow">{info.step.source} → {info.step.dest}</span>
        </div>
      </div>
      <div className="loop-next-banner-countdown">{nextStepLabel(info)}</div>
    </div>
  );
};

// ─── Loop Engine: one card per subscribed loop ───
// Per-loop actions: "+ Log movement" opens the LogMovement modal pre-set
// to this loop's first step. The whole card is clickable and opens the
// slide-in side panel; inner buttons stopPropagation so they don't double-fire.
// T-043: allocations retired — the footer chips are pure member filters now
// (they highlight that member's execution dots on the chain + scope the
// activity feed), no planned-$ figures.
const LoopEngine = ({ loop, members, focusedMember, setFocusedMember, onLogMovement, onShowDetails }) => {
  const loopMembers = members;
  const openDetails = () => onShowDetails && onShowDetails(loop);
  const stop = (fn) => (e) => { e.stopPropagation(); fn && fn(e); };

  return (
    <section
      className="card loop-card is-clickable"
      role="button"
      tabIndex={0}
      aria-label={`Open loop details: ${loop.name}`}
      onClick={openDetails}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetails(); } }}
    >
      <header className="card-header">
        <LoopHeroLogo loop={loop} />
        <div className="card-title-block">
          <div className="card-eyebrow">Subscribed loop{loop.paused ? ' · paused' : ''}</div>
          <h2 className="card-title">{loop.name}</h2>
          {loop.description && <p className="card-desc">{loop.description}</p>}
        </div>
        <div className="loop-meta">
          <span className="btn-ghost-pill" aria-hidden="true">Details →</span>
          <button type="button" className="btn-primary-sm" onClick={stop(() => onLogMovement && onLogMovement(loop))}>+ Log movement</button>
        </div>
      </header>

      {/* Next action gets its own row so it reads as a primary CTA on the
          card, not a quiet pill in the title block. Loop facts moved into
          the side panel earlier; this slot now belongs to the next-step
          banner alone. */}
      <LoopNextStepBanner loop={loop} />

      <LoopChain loop={loop} members={loopMembers} focusedMember={focusedMember} />

      <footer className="loop-footer">
        <div className="loop-allocations">
          <span className="alloc-label">Household</span>
          {loopMembers.map(m => (
            <button
              key={m.id}
              type="button"
              className={'alloc-chip' + (focusedMember === m.id ? ' is-focused' : '')}
              onClick={stop(() => setFocusedMember(focusedMember === m.id ? null : m.id))}
              style={{ '--chip-color': m.color }}
              title={`Highlight ${m.name}'s activity on this loop`}
            >
              <Avatar member={m} size={20} />
              <span className="alloc-name">{m.name}</span>
            </button>
          ))}
        </div>
      </footer>
    </section>
  );
};

// Hero logo for a loop. Falls back to a kind-colored initial tile derived
// from the first step's source kind. When the admin sets loop.heroLogoUrl
// (e.g. a Coinbase wordmark) we render it inside the same tile.
const LoopHeroLogo = ({ loop, size = 56 }) => {
  const firstStep = loop?.steps?.[0];
  const slug = firstStep?.sourceKind || firstStep?.destKind || loop?.id || '';
  const color = kindColor(slug);
  const fallbackInit = kindInitial(firstStep?.source || loop?.name || '?');
  return (
    <div
      className={'loop-hero-logo' + (loop?.heroLogoUrl ? ' has-logo' : '')}
      style={{ '--logo-color': color, width: size, height: size }}
      aria-hidden="true"
    >
      {loop?.heroLogoUrl
        ? <img src={loop.heroLogoUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.remove('has-logo'); e.currentTarget.parentElement.textContent = fallbackInit; }} />
        : fallbackInit}
    </div>
  );
};

// Read-only strip of the admin-editable "facts" — used inside the loop
// details side panel (for both members and admins) and on the admin
// catalog/overview loop cards. The member Home card intentionally
// doesn't render this anymore: same data is one click away in the
// side panel, which kept the home loop cards cluttered.
//
// Layout is a tidy spec-sheet: one row per attribute, label flush left,
// value flush right. Payment-method icons (Apple Pay, etc.) sit next to
// the value text — clarifies which method the icon represents instead
// of looking like an ornament next to the word "Payment".
//
// Hidden entirely when no facts are filled in yet (no noisy "—" row).
const LoopFactsStrip = ({ loop }) => {
  const facts = collectLoopFacts(loop);
  if (!facts.length) return null;
  return (
    <div className="loop-facts-strip">
      {facts.map((f) => (
        <div key={f.key} className="lfs-item" title={f.label}>
          <span className="lfs-lbl">{f.label}</span>
          <span className="lfs-val">
            {f.icon && <span className="lfs-icon">{f.icon}</span>}
            {f.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Collect non-empty admin facts in display order. Returns [{ key, label, value, icon }].
// Used by both the on-card strip and the details panel's read view.
// paymentMethods is an array — when populated we show every selected method
// inline (icons + comma-separated label).
function collectLoopFacts(loop) {
  if (!loop) return [];
  const out = [];
  // Throughput uses the exact dollar amount (fmt$) instead of the
  // K-shortened fmt$Big so admin-entered values like $25,347 don't
  // silently compress to "$25.3K" on display. The unit suffix (per
  // cycle/week/month) tells investors how to read the number — a
  // critical disambiguation for the catalog math.
  if (loop.maxThroughput != null) {
    const unit = loop.maxThroughputUnit || 'per_cycle';
    const suffix = unit === 'per_month' ? '/mo' : unit === 'per_week' ? '/wk' : '/cyc';
    out.push({ key: 'mt', label: 'Max throughput', value: `${fmt$(loop.maxThroughput)} ${suffix}` });
  }
  if (loop.velocityDays != null) {
    // Auto-derived velocity may carry biz-day conversion fractions
    // (e.g. 1 biz + 2 cal = 3.4 cal days). Round to 1 decimal place
    // and strip trailing zeros so "7.0" reads as "7" but "7.4" stays.
    const days = Math.round(Number(loop.velocityDays) * 10) / 10;
    out.push({ key: 'v', label: 'Velocity', value: `${days} day${days === 1 ? '' : 's'}` });
  }
  // Rate + APY are split into TWO separate facts so the per-cycle rate
  // always renders even when velocity_days is unset (no APY possible).
  // Previous behavior collapsed them into one ambiguous "APY or rate"
  // line that vanished entirely when velocity_days was null AND
  // blendedRate was 0 — and was visually confusing otherwise. Now:
  //   • Rate / cycle  → always shown when blendedRate > 0
  //   • APY (auto)    → only shown when velocity_days is set
  const perCycle = Number(loop.blendedRate || 0);
  if (perCycle > 0) {
    out.push({ key: 'rate', label: 'Rate / cycle', value: fmtPct(perCycle) });
    if (loop.velocityDays > 0) {
      const apy = perCycle * (365 / Number(loop.velocityDays));
      out.push({ key: 'apy', label: 'APY (auto)', value: fmtPct(apy, 0) });
    }
  }
  if (loop.devicesNeeded != null) out.push({ key: 'd',  label: 'Devices needed', value: String(loop.devicesNeeded) });
  const methods = Array.isArray(loop.paymentMethods) ? loop.paymentMethods : [];
  if (methods.length) {
    const labels = methods.map((v) => (PAYMENT_METHODS.find((x) => x.value === v)?.label || v));
    out.push({
      key: 'pm',
      label: methods.length === 1 ? 'Payment' : 'Payments',
      value: labels.join(' · '),
      icon: <span className="lfs-pm-icons">{methods.map((v) => <PaymentMethodIcon key={v} method={v} size={14} />)}</span>,
    });
  }
  return out;
}

// Payment-method options shared between the on-card strip and the
// edit form. value matches the loops.payment_method CHECK constraint.
const PAYMENT_METHODS = [
  { value: 'debit',       label: 'Debit (TK)'  },
  { value: 'apple_pay',   label: 'Apple Pay'   },
  { value: 'google_pay',  label: 'Google Pay'  },
  { value: 'samsung_pay', label: 'Samsung Pay' },
];

// Loop theme accents — mirrors the legacy workbench's INV_LOOP_THEMES and
// the name→hex maps in useCatalogData/StepDetail. Stored on loops.theme_color.
const LOOP_THEMES = ['coral', 'teal', 'purple', 'amber', 'green', 'blue', 'rose', 'indigo'];

// Compact glyph for each payment method. Pure inline SVG/text so we don't
// depend on external logo files — close enough to the brand mark to read.
const PaymentMethodIcon = ({ method, size = 18 }) => {
  const s = Number(size);
  if (method === 'apple_pay') {
    return (
      <span className="pm-icon pm-apple" style={{ width: s, height: s }} aria-hidden="true">
        <svg viewBox="0 0 24 24" width={s * 0.7} height={s * 0.7} fill="currentColor">
          <path d="M16.4 1.6c-1 .1-2.2.7-2.9 1.6-.6.8-1.2 2-1 3.2 1.1.1 2.3-.6 3-1.4.7-.8 1.2-2 .9-3.4Zm3.4 16.7c-.5 1.2-.8 1.7-1.5 2.8-.9 1.5-2.3 3.3-4 3.3-1.5 0-1.9-1-4-1-2 0-2.5 1-4 1-1.7 0-3-1.7-4-3.2-2.7-4.2-3-9-1.3-11.6 1.2-1.8 3.1-2.9 4.9-2.9 1.8 0 3 1 4.5 1 1.5 0 2.4-1 4.5-1 1.6 0 3.3.9 4.4 2.4-3.9 2.2-3.3 7.9.5 9.2Z"/>
        </svg>
      </span>
    );
  }
  if (method === 'google_pay') {
    return (
      <span className="pm-icon pm-gpay" style={{ width: s * 1.6, height: s }} aria-hidden="true">
        <span className="pm-gpay-g" style={{ fontSize: s * 0.62 }}>G</span>
        <span className="pm-gpay-pay" style={{ fontSize: s * 0.55 }}>Pay</span>
      </span>
    );
  }
  if (method === 'samsung_pay') {
    return (
      <span className="pm-icon pm-spay" style={{ width: s * 1.6, height: s }} aria-hidden="true">
        <span className="pm-spay-s" style={{ fontSize: s * 0.62 }}>S</span>
        <span className="pm-spay-pay" style={{ fontSize: s * 0.55 }}>Pay</span>
      </span>
    );
  }
  // 'debit' — render a Taekus-style TK chip.
  return (
    <span className="pm-icon pm-tk" style={{ width: s, height: s, fontSize: s * 0.5 }} aria-hidden="true">TK</span>
  );
};

// T-043: LoopReturnProjection (the inline allocation editor + live
// return projection) is retired with the allocations concept. The panel's
// summary strip shows actuals (cycles, value YTD, rate); planning math on
// planned allocations was projection fiction — money math always used
// actuals (T-021). Per-loop monthly cost templates died earlier with the
// D-15 cost redesign (schedules + items are the cost model now).

// ─── Admin loop-builder tools ───
// LoopDetailsPanel renders these only when the surface passes `adminTools`
// ({ steps, kinds, refs, assignments, holders, onChanged }) — today that's
// Catalog admin. Ported from the legacy Settings → Investment Catalog
// workbench (T-004/T-005) so the module is self-sufficient.
const LoopStepManager = ({ loop, tools }) => {
  const { steps: allSteps, kinds, refs, onChanged } = tools;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState('');
  const myRefs = refs.filter((r) => r.loop_id === loop.id).sort((a, b) => a.step_order - b.step_order);
  const stepById = Object.fromEntries(allSteps.map((s) => [s.id, s]));
  const kindLabel = (slug) => kinds.find((k) => k.name === slug)?.label || slug;
  const inLoop = new Set(myRefs.map((r) => r.step_id));
  const available = allSteps.filter((s) => s.status === 'active' && !inLoop.has(s.id));

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); }
    catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const addStep = () => run(async () => {
    if (!pick) return;
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase client unavailable');
    const nextOrder = myRefs.length ? Math.max(...myRefs.map((r) => r.step_order)) + 1 : 1;
    const { error } = await sb.from('loop_step_refs').insert({ loop_id: loop.id, step_id: pick, step_order: nextOrder });
    if (error) throw error;
    setPick('');
  });

  const removeRef = (ref) => run(async () => {
    if (!window.confirm('Remove this step from the loop? Existing value events that reference it are unaffected.')) return;
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase client unavailable');
    const { error } = await sb.from('loop_step_refs').delete().eq('id', ref.id);
    if (error) throw error;
    // Renumber survivors back to 1..n. Two phases through a scratch range
    // so UNIQUE(loop_id, step_order) never collides mid-shuffle.
    const remaining = myRefs.filter((r) => r.id !== ref.id);
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i].step_order === i + 1) continue;
      const { error: e1 } = await sb.from('loop_step_refs').update({ step_order: 1000 + i + 1 }).eq('id', remaining[i].id);
      if (e1) throw e1;
    }
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i].step_order === i + 1) continue;
      const { error: e2 } = await sb.from('loop_step_refs').update({ step_order: i + 1 }).eq('id', remaining[i].id);
      if (e2) throw e2;
    }
  });

  const move = (ref, dir) => run(async () => {
    const idx = myRefs.findIndex((r) => r.id === ref.id);
    const other = myRefs[idx + dir];
    if (!other) return;
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase client unavailable');
    // Three-update swap via the scratch range (same UNIQUE dodge).
    const updates = [
      { id: ref.id,   step_order: 1000 + ref.step_order },
      { id: other.id, step_order: ref.step_order },
      { id: ref.id,   step_order: other.step_order },
    ];
    for (const u of updates) {
      const { error } = await sb.from('loop_step_refs').update({ step_order: u.step_order }).eq('id', u.id);
      if (error) throw error;
    }
  });

  return (
    <div className="ldp-facts-block">
      <div className="ldp-section-eye-row">
        <span className="ldp-section-eye">Steps in this loop</span>
      </div>
      {myRefs.length === 0 && <div className="ldp-facts-empty">No steps yet — append the first one below.</div>}
      {myRefs.map((r, i, all) => {
        const s = stepById[r.step_id] || {};
        // Same numbering the loop is drawn with (1B / 1C for a card branch).
        const label = labelLoopSteps(all.map((x) => stepById[x.step_id] || {}))[i].label;
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-1, #eef0f6)', fontSize: 13 }}>
            <span className="mono" style={{ color: 'var(--text-3)', width: 24 }}>{label}.</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              {kindLabel(s.source_kind)} → {kindLabel(s.dest_kind)}
              <span className="mono" style={{ color: 'var(--text-3)', marginLeft: 6 }}>{fmtPct(Number(s.value_rate || 0))}</span>
            </span>
            <button type="button" className="btn-ghost" disabled={busy || i === 0} onClick={() => move(r, -1)} aria-label="Move up" style={{ padding: '2px 8px' }}>↑</button>
            <button type="button" className="btn-ghost" disabled={busy || i === myRefs.length - 1} onClick={() => move(r, 1)} aria-label="Move down" style={{ padding: '2px 8px' }}>↓</button>
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => removeRef(r)} style={{ padding: '2px 8px', color: 'var(--rose, #e11d48)' }}>Remove</button>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
        {available.length === 0 ? (
          <div style={{ color: 'var(--text-3)', fontSize: 12 }}>All active catalog steps are already in this loop. Create new steps in the Steps tab first.</div>
        ) : (
          <>
            <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy} style={{ flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
              <option value="">Pick a step…</option>
              {available.map((s) => (
                <option key={s.id} value={s.id}>
                  {kindLabel(s.source_kind)} → {kindLabel(s.dest_kind)} · {fmtPct(Number(s.value_rate || 0))}
                </option>
              ))}
            </select>
            <button type="button" className="btn-primary" disabled={busy || !pick} onClick={addStep} style={{ padding: '7px 14px', flexShrink: 0 }}>Append to end</button>
          </>
        )}
      </div>
      {err && <div className="ldp-facts-error" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
};

const LoopAssignmentManager = ({ loop, tools }) => {
  const { assignments, holders, onChanged } = tools;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const mine = assignments.filter((a) => a.loop_id === loop.id);
  const nameOf = (id) => { const h = holders.find((x) => x.id === id); return h?.display_name || h?.name || id; };
  const unassigned = holders.filter((h) => !mine.some((a) => a.member_id === h.id));

  const run = async (fn) => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); }
    catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const assign = (memberId) => run(async () => {
    if (!memberId) return;
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase client unavailable');
    const { error } = await sb.from('loop_assignments').insert({ loop_id: loop.id, member_id: memberId });
    if (error) throw error;
  });

  const unassign = (a) => run(async () => {
    // T-017: the DB trigger deactivates the household's subscription (and
    // its allocations) when the assignment row is deleted.
    if (!window.confirm(`Unassign "${loop.name}" from ${nameOf(a.member_id)}?\n\nThis also unsubscribes their household — the loop disappears from their app. History stays. Re-assigning re-activates it.`)) return;
    const sb = getSupabase();
    if (!sb) throw new Error('Supabase client unavailable');
    const { error } = await sb.from('loop_assignments').delete().eq('id', a.id);
    if (error) throw error;
  });

  return (
    <div className="ldp-facts-block">
      <div className="ldp-section-eye-row">
        <span className="ldp-section-eye">Assigned to</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {mine.length === 0 && <span style={{ color: 'var(--text-3)', fontSize: 12 }}>Not assigned yet — assigning a holder subscribes their household immediately.</span>}
        {mine.map((a) => (
          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent-bg, #eef2ff)', color: 'var(--accent-2, #3730a3)', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
            {nameOf(a.member_id)}
            <button type="button" onClick={() => unassign(a)} disabled={busy} aria-label={`Unassign ${nameOf(a.member_id)}`}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
          </span>
        ))}
        {unassigned.length > 0 ? (
          <select value="" onChange={(e) => { assign(e.target.value); e.target.value = ''; }} disabled={busy}
            style={{ padding: '5px 8px', border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 999, fontSize: 12, fontFamily: 'inherit', background: '#fff' }}>
            <option value="">+ Assign…</option>
            {unassigned.map((h) => <option key={h.id} value={h.id}>{h.display_name || h.name}</option>)}
          </select>
        ) : (
          mine.length > 0 && <span style={{ color: 'var(--text-3)', fontSize: 12 }}>all holders assigned</span>
        )}
      </div>
      {err && <div className="ldp-facts-error" style={{ marginTop: 8 }}>{err}</div>}
    </div>
  );
};

// ─── Loop details slide-in panel ───
// Opens when the user clicks anywhere on a loop card. Read-only by default;
// when the viewer is an admin we expose an "Edit facts" toggle that turns
// the hero-logo URL + 5 admin-editable facts (max throughput, velocity,
// rate of return, devices needed, payment method) into a form that writes
// back to the loops table.
const LoopDetailsPanel = ({ loop, members, onClose, isAdmin = false, onLoopUpdated, onLoopDeleted, navigate, defaultEditing = false, adminTools = null }) => {
  const [editing, setEditing] = useState(defaultEditing);
  const [draft, setDraft] = useState(() => loopToDraft(loop));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Separate confirm + saving + error state for the household-level
  // unsubscribe so it doesn't collide with the admin "edit facts" save
  // flow. Both can coexist on the same panel for an admin viewing their
  // own subscribed loop.
  const [confirmUnsub, setConfirmUnsub] = useState(false);
  const [unsubSaving, setUnsubSaving] = useState(false);
  const [unsubError, setUnsubError] = useState(null);

  useEffect(() => { setDraft(loopToDraft(loop)); setEditing(defaultEditing); setError(null); setConfirmDelete(false); setConfirmUnsub(false); setUnsubError(null); }, [loop?.id, defaultEditing]);

  if (!loop) return null;
  const facts = collectLoopFacts(loop);

  // Persist all admin-editable fields in one PATCH so name + description +
  // active + the headline facts round-trip together. The "edit facts"
  // copy on the trigger button is now a misnomer — semantically it
  // edits the whole loop — but renaming the prop would ripple, so the
  // handler is renamed instead.
  const saveLoop = async () => {
    if (!(draft.name || '').trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');
      // parseFloat(toFixed(N)) trims float-multiplication garbage without
      // rounding the user's intended precision. e.g. UI input "3.25" %
      // → 3.25/100 = 0.0325 cleanly, instead of 0.0324999999999999.
      const cleanFloat = (s, places = 10) => {
        if (s === '' || s == null || !Number.isFinite(Number(s))) return null;
        return parseFloat(Number(s).toFixed(places));
      };
      const cleanInt = (s) => {
        if (s === '' || s == null) return null;
        const n = parseInt(s, 10);
        return Number.isFinite(n) ? n : null;
      };
      // rate_of_return AND velocity_days intentionally NOT in this
      // payload — both are auto-derived from the steps. Rate is the sum
      // of step.value_rate per cycle; velocity is the sum of
      // (step_duration + wait_duration) across all steps, with biz days
      // converted to cal days. Manual editing was creating conflicts
      // with the computed values.
      const VALID_UNITS = ['per_cycle', 'per_week', 'per_month'];
      const { error: err } = await sb.from('loops').update({
        name:                   draft.name.trim(),
        description:            draft.description.trim() || null,
        active:                 !!draft.active,
        hero_logo_url:          draft.heroLogoUrl.trim() || null,
        max_throughput:         cleanFloat(draft.maxThroughput),
        max_throughput_unit:    VALID_UNITS.includes(draft.maxThroughputUnit) ? draft.maxThroughputUnit : 'per_cycle',
        devices_needed:         cleanInt(draft.devicesNeeded),
        monthly_operating_cost: cleanFloat(draft.monthlyOperatingCost) ?? 0,
        payment_methods:        Array.isArray(draft.paymentMethods) ? draft.paymentMethods : [],
        ...(draft.themeColor !== undefined ? { theme_color: draft.themeColor || null } : {}),
      }).eq('id', loop.id);
      if (err) throw err;
      setEditing(false);
      if (typeof onLoopUpdated === 'function') onLoopUpdated();
      else if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  // Household-level "unsubscribe from this loop" — sets the
  // loop_subscriptions row to active=false. Past value_events stay; the
  // trigger stops firing new events for this (holder, loop) because
  // process_value_event only matches active subscriptions. Reversible by
  // re-assigning the loop.
  const unsubscribeFromLoop = async () => {
    if (!loop?.subscriptionId) { setUnsubError('No active subscription found for this loop.'); return; }
    setUnsubSaving(true); setUnsubError(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');
      const { error: err } = await sb.from('loop_subscriptions')
        .update({ active: false })
        .eq('id', loop.subscriptionId);
      if (err) throw err;
      // Trigger a reload so the loop disappears from Home immediately.
      if (typeof onLoopUpdated === 'function') onLoopUpdated();
      else if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
      onClose && onClose();
    } catch (e) {
      setUnsubError(e?.message || String(e));
      setUnsubSaving(false);
    }
  };

  return (
    <div onClick={onClose} className="member-edit-backdrop" role="presentation">
      <aside
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Loop details: ${loop.name}`}
        className="member-edit-panel"
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
          <LoopHeroLogo loop={loop} size={52} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent, #4f46e5)' }}>
              Loop details
            </div>
            <h2 style={{ margin: '4px 0 4px', fontSize: 20, fontWeight: 800 }}>{loop.name}</h2>
            {loop.description && <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)' }}>{loop.description}</p>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {/* Primary actions live at the top so they're findable without
                scrolling past the steps + projection blocks. */}
            {isAdmin && !editing && (
              <button type="button" className="btn-ghost-pill" onClick={() => setEditing(true)} style={{ padding: '6px 12px' }}>
                Edit loop
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="Close"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3)', padding: 4, marginTop: -2 }}>✕</button>
          </div>
        </div>

        {/* Loop-level summary */}
        <div className="ldp-summary">
          <div className="ldp-stat"><div className="ldp-stat-lbl">Cycles complete</div><div className="ldp-stat-val">{loop.cyclesCompleted}</div></div>
          <div className="ldp-stat"><div className="ldp-stat-lbl">In progress</div><div className="ldp-stat-val">{loop.cyclesInProgress}</div></div>
          <div className="ldp-stat"><div className="ldp-stat-lbl">Value YTD</div><div className="ldp-stat-val ldp-stat-green">{fmt$(loop.valueYTD)}</div></div>
          <div className="ldp-stat"><div className="ldp-stat-lbl">Rate / cycle</div><div className="ldp-stat-val">{fmtPct(loop.blendedRate)}</div></div>
        </div>

        {/* Admin-editable loop fields (name, description, active, facts) */}
        <div className="ldp-facts-block">
          <div className="ldp-section-eye-row">
            <span className="ldp-section-eye">{editing ? 'Edit loop' : 'Loop facts'}</span>
          </div>
          {!editing ? (
            facts.length === 0 ? (
              <div className="ldp-facts-empty">
                No facts entered yet.{isAdmin ? <> <button type="button" className="ldp-facts-empty-btn" onClick={() => setEditing(true)}>Add them →</button></> : null}
              </div>
            ) : (
              // Reuse the same tidy spec-sheet strip we render on admin
              // loop cards — one row per attribute, value flush right,
              // payment-method icons next to their value.
              <LoopFactsStrip loop={loop} />
            )
          ) : (
            <div className="ldp-facts-edit">
              <LDPField label="Name">
                <input type="text" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={saving} placeholder="e.g. Crypto Yield Cycle" required autoFocus />
              </LDPField>
              <LDPField label="Description" hint="One sentence shown to investors below the name.">
                <textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} disabled={saving} placeholder="What this loop does, in one line" />
              </LDPField>
              <LDPField label="Status">
                <label className="ldp-checkbox-row">
                  <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} disabled={saving} />
                  <span>Active — visible to investors in the catalog</span>
                </label>
              </LDPField>
              <LDPField label="Hero logo URL" hint="e.g. /coinbase.svg, or a full https:// URL">
                <input type="url" value={draft.heroLogoUrl} onChange={(e) => setDraft({ ...draft, heroLogoUrl: e.target.value })} disabled={saving} placeholder="https://…" />
              </LDPField>
              <LDPField label="Maximum throughput ($)" hint="The unit drives how the catalog computes capital required + APY. Per-cycle = $X per cycle. Per-week = $X total volume per week. Per-month = $X total volume per month.">
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    {...NO_WHEEL}
                    type="number"
                    min={0}
                    step="any"
                    value={draft.maxThroughput}
                    onChange={(e) => setDraft({ ...draft, maxThroughput: e.target.value })}
                    disabled={saving}
                    placeholder="e.g. 25000"
                    inputMode="decimal"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <select
                    value={draft.maxThroughputUnit || 'per_cycle'}
                    onChange={(e) => setDraft({ ...draft, maxThroughputUnit: e.target.value })}
                    disabled={saving}
                    style={{ padding: '8px 10px', border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}
                  >
                    <option value="per_cycle">/ cycle</option>
                    <option value="per_week">/ week</option>
                    <option value="per_month">/ month</option>
                  </select>
                </div>
              </LDPField>
              <LDPField label="Monthly operating cost ($)" hint="Fixed monthly cost at max throughput — independent of who subscribes. Shows in the browse catalog's net/APY at max.">
                <input {...NO_WHEEL} type="number" min={0} step="any" value={draft.monthlyOperatingCost} onChange={(e) => setDraft({ ...draft, monthlyOperatingCost: e.target.value })} disabled={saving} placeholder="e.g. 300" inputMode="decimal" />
              </LDPField>
              {/* Velocity (days per cycle) is auto-derived from each
                  step's step_duration + wait_duration (with biz days
                  converted to cal days). Edit step timings in the Steps
                  tab of the Catalog admin if you want to change it. */}
              <LDPField label="Number of devices needed">
                <input {...NO_WHEEL} type="number" min={0} step={1} value={draft.devicesNeeded} onChange={(e) => setDraft({ ...draft, devicesNeeded: e.target.value })} disabled={saving} placeholder="e.g. 2" inputMode="numeric" />
              </LDPField>
              <LDPField label="Payment methods" hint="Tap each method this loop accepts. You can pick more than one.">
                <div className="ldp-pm-options">
                  {PAYMENT_METHODS.map((m) => {
                    const selected = (draft.paymentMethods || []).includes(m.value);
                    return (
                      <button
                        key={m.value}
                        type="button"
                        className={'ldp-pm-opt' + (selected ? ' is-selected' : '')}
                        onClick={() => {
                          const cur = draft.paymentMethods || [];
                          const next = selected ? cur.filter((v) => v !== m.value) : [...cur, m.value];
                          setDraft({ ...draft, paymentMethods: next });
                        }}
                        disabled={saving}
                        aria-pressed={selected}
                      >
                        <PaymentMethodIcon method={m.value} size={22} />
                        <span className="ldp-pm-label">{m.label}</span>
                        {selected && <span className="ldp-pm-check" aria-hidden="true">✓</span>}
                      </button>
                    );
                  })}
                  {(draft.paymentMethods || []).length > 0 && (
                    <button
                      type="button"
                      className="ldp-pm-opt ldp-pm-clear"
                      onClick={() => setDraft({ ...draft, paymentMethods: [] })}
                      disabled={saving}
                    >
                      <span className="ldp-pm-none">×</span>
                      <span className="ldp-pm-label">Clear</span>
                    </button>
                  )}
                </div>
              </LDPField>
              {draft.themeColor !== undefined && (
                <LDPField label="Theme color" hint="Accent used for the loop glyph + step-detail chrome.">
                  <select
                    value={draft.themeColor}
                    onChange={(e) => setDraft({ ...draft, themeColor: e.target.value })}
                    disabled={saving}
                    style={{ padding: '8px 10px', border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff' }}
                  >
                    <option value="">None</option>
                    {LOOP_THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </LDPField>
              )}
              {error && <div className="ldp-facts-error">{error}</div>}
              <div className="ldp-facts-actions">
                <button type="button" className="btn-ghost" disabled={saving} onClick={() => { setDraft(loopToDraft(loop)); setEditing(false); setError(null); }} style={{ padding: '7px 14px' }}>Cancel</button>
                <button type="button" className="btn-primary" disabled={saving} onClick={saveLoop} style={{ padding: '7px 14px' }}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>

              {/* Danger zone — delete the loop entirely. Confirmed by a
                  second click. Cascades via the loops_step_refs FK
                  (ON DELETE CASCADE), but subscriptions are NOT
                  cascaded — we block deletion if any exist. */}
              <div className="ldp-danger-zone">
                <div className="ldp-danger-eyebrow">Danger zone</div>
                {!confirmDelete ? (
                  <button type="button" disabled={saving} onClick={() => setConfirmDelete(true)} className="ldp-danger-btn">
                    Delete this loop
                  </button>
                ) : (
                  <div className="ldp-danger-confirm">
                    <span>Sure? This removes the loop and its step assignments.</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" disabled={saving} onClick={async () => {
                        setSaving(true); setError(null);
                        try {
                          const sb = getSupabase();
                          if (!sb) throw new Error('Supabase client unavailable');
                          // Reject if any subscriptions reference this loop.
                          const { count: subCount } = await sb.from('loop_subscriptions').select('id', { count: 'exact', head: true }).eq('loop_id', loop.id);
                          if ((subCount || 0) > 0) throw new Error(`${subCount} active subscription(s) reference this loop. Unsubscribe them first.`);
                          const { error: err } = await sb.from('loops').delete().eq('id', loop.id);
                          if (err) throw err;
                          if (typeof onLoopDeleted === 'function') onLoopDeleted();
                          else if (typeof onLoopUpdated === 'function') onLoopUpdated();
                          onClose && onClose();
                        } catch (e) { setError(e?.message || String(e)); setSaving(false); }
                      }} className="ldp-danger-btn-confirm">
                        {saving ? 'Deleting…' : 'Yes, delete'}
                      </button>
                      <button type="button" disabled={saving} onClick={() => setConfirmDelete(false)} className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Per-step detail */}
        <div className="ldp-section-eye">Steps</div>
        <div className="ldp-step-list">
          {loopPositions(loop.steps).map((p) => {
          // Branch positions (Step 1B / 1C — the same load on either card)
          // group their alternatives under one caption with an "or" between.
          const cards = p.steps.map((step) => {
            const amt = fmtStepAmount(step.amount);
            const dur = fmtStepDays(step.stepDurationDays, step.stepDurationIsBusiness);
            const wait = fmtWaitDays(step.waitDurationDays, step.waitDurationIsBusiness);
            const isZero = step.rate === 0;
            return (
              <div key={step.idx} className="ldp-step">
                <div className="ldp-step-head">
                  <span className="ldp-step-num">Step {step.label || step.idx}</span>
                  <span className={'ldp-step-rate' + (isZero ? ' is-zero' : '')}>{fmtPct(step.rate)}</span>
                </div>
                {/* Source → dest cards with brand logos — same visual
                    treatment the catalog DetailPanel uses, so the
                    subscribed-loop side panel finally matches the
                    polish of the browse-catalog one. */}
                <div className="ldp-step-flow ldp-step-flow-cards">
                  <AcctCard kind="from" name={step.source} institution={step.sourceInstitution} kindSlug={step.sourceKind} logoUrl={step.sourceLogo} />
                  <StepArrow gradId={`ldp-grad-${loop.id || loop.name}-${step.idx}`} fromKind={step.sourceKind} toKind={step.destKind} />
                  <AcctCard kind="to"   name={step.dest}   institution={step.destInstitution}   kindSlug={step.destKind} logoUrl={step.destLogo} />
                </div>
                <div className="ldp-step-meta">
                  <div><span>Amount</span><strong className={amt.italic ? 'is-italic' : ''}>{amt.text}</strong></div>
                  <div><span>Step takes</span><strong className={dur.italic ? 'is-italic' : ''}>{dur.text}</strong></div>
                  <div><span>Wait after</span><strong className={wait.muted ? 'is-muted' : ''}>{wait.text}</strong></div>
                  <div><span>Moves YTD</span><strong>{step.movesYTD}</strong></div>
                  <div><span>Value YTD</span><strong className="ldp-money">{fmt$(step.valueYTD)}</strong></div>
                </div>
                {members.length > 0 && (
                  <div className="ldp-step-members">
                    {members.map((m) => {
                      const count = step.executed[m.id] || 0;
                      return (
                        <div key={m.id} className="ldp-step-member">
                          <Avatar member={m} size={22} />
                          <span className="ldp-step-member-name">{m.name}</span>
                          <span className="ldp-step-member-count">{count}× executed</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          });
          if (cards.length === 1) return cards[0];
          return (
            <div key={'pos-' + p.position} className="ldp-step-branch">
              <div className="ldp-step-branch-cap">Step {p.position} · either card into {p.steps[0].dest}</div>
              {cards.map((c, j) => (
                <Fragment key={j}>
                  {j > 0 && <div className="ldp-step-or">or</div>}
                  {c}
                </Fragment>
              ))}
            </div>
          );
          })}
        </div>

        {/* Admin loop-builder: manage which catalog steps make up this
            loop + which holders it's assigned to. Only rendered when the
            surface provides adminTools (Catalog admin). */}
        {isAdmin && adminTools && (
          <>
            <LoopStepManager loop={loop} tools={adminTools} />
            <LoopAssignmentManager loop={loop} tools={adminTools} />
          </>
        )}

        {/* Household-level unsubscribe. Only renders when there's an
            active subscription (i.e. the holder/admin is viewing a loop
            they're enrolled in — not while browsing an unsubscribed
            loop from the catalog). Destructive; requires a confirm. */}
        {loop.subscriptionId && (
          <div className="ldp-unsub-zone">
            <div className="ldp-unsub-eyebrow">Subscription</div>
            <div className="ldp-unsub-body">
              {!confirmUnsub ? (
                <>
                  <div className="ldp-unsub-copy">
                    Unsubscribing stops new value events from firing on this loop for the entire household. Past events and settlements stay intact, and you can re-subscribe later from the catalog.
                  </div>
                  <button
                    type="button"
                    className="ldp-unsub-btn"
                    onClick={() => { setConfirmUnsub(true); setUnsubError(null); }}
                    disabled={unsubSaving}
                  >Unsubscribe from this loop</button>
                </>
              ) : (
                <div className="ldp-unsub-confirm">
                  <span className="ldp-unsub-confirm-q">Unsubscribe the whole household from {loop.name}?</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      className="ldp-unsub-confirm-yes"
                      onClick={unsubscribeFromLoop}
                      disabled={unsubSaving}
                    >{unsubSaving ? 'Unsubscribing…' : 'Yes, unsubscribe'}</button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setConfirmUnsub(false)}
                      disabled={unsubSaving}
                      style={{ padding: '6px 12px', fontSize: 12 }}
                    >Cancel</button>
                  </div>
                </div>
              )}
              {unsubError && <div className="ldp-facts-error" style={{ marginTop: 8 }}>{unsubError}</div>}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="btn-primary" onClick={onClose} style={{ padding: '8px 16px' }}>
            Done
          </button>
        </div>
      </aside>
    </div>
  );
};

// Spread this onto any <input type="number"> to prevent the browser's
// "scroll wheel increments the value" behavior, which has bitten admins
// by silently snapping a freshly-typed 300 → 299 (or similar) after a
// stray wheel scroll while the input still had focus. Blurring on
// wheel kills the change without disrupting normal typing.
const NO_WHEEL = { onWheel: (e) => e.currentTarget.blur() };

// Reset a draft-form snapshot from a loop. UI displays rate_of_return as a
// percentage (e.g. 3.25) even though the DB stores it as a decimal (0.0325).
// paymentMethods is always an array (possibly empty) in the draft.
//
// Number round-trips use `parseFloat(toFixed(N))` to strip JS float garbage
// (e.g. `0.032 * 100 === 3.2000000000000006`) without rounding away precision
// the user intentionally entered. toFixed(8) keeps up to 6 decimal places of
// the percent display, which is more than anyone will ever need but enough
// to survive any reasonable input.
const cleanNum = (n) => {
  if (n == null || n === '' || !Number.isFinite(Number(n))) return '';
  return String(parseFloat(Number(n).toFixed(8)));
};
function loopToDraft(loop) {
  if (!loop) return { name: '', description: '', active: true, heroLogoUrl: '', maxThroughput: '', maxThroughputUnit: 'per_cycle', devicesNeeded: '', monthlyOperatingCost: '', paymentMethods: [] };
  return {
    name:           loop.name || '',
    description:    loop.description || '',
    // `active` defaults to true when the loop row doesn't include it (e.g.
    // a loop passed in from useInvestmentData which only loads active ones).
    active:         loop.active !== false,
    heroLogoUrl:    loop.heroLogoUrl || '',
    maxThroughput:  cleanNum(loop.maxThroughput),
    maxThroughputUnit: loop.maxThroughputUnit || 'per_cycle',
    // velocityDays is auto-derived from steps — not editable in the form.
    // rate_of_return is no longer admin-editable — it's auto-derived from
    // the steps (per-cycle rate) and velocity_days (annualized APY). See
    // collectLoopFacts() for the display path.
    devicesNeeded:  loop.devicesNeeded == null ? '' : String(loop.devicesNeeded),
    // Loop-level fixed cost at max throughput. Catalog max-throughput
    // economics subtract this from gross to compute net at max.
    monthlyOperatingCost: cleanNum(loop.monthlyOperatingCost),
    paymentMethods: Array.isArray(loop.paymentMethods) ? [...loop.paymentMethods] : [],
    // Only surfaces that load loops.theme_color pass it through (Catalog
    // admin). Elsewhere it stays undefined so a save can't wipe the theme.
    themeColor:     loop.themeColor === undefined ? undefined : (loop.themeColor || ''),
  };
}

// Labeled field wrapper used inside LoopDetailsPanel's edit mode. Pure
// presentation — slot the input via children.
const LDPField = ({ label, hint, children }) => (
  <label className="ldp-field">
    <span className="ldp-field-lbl">{label}</span>
    {children}
    {hint && <small className="ldp-field-hint">{hint}</small>}
  </label>
);

// ─── Kind-deterministic color/initial helpers (mirror parent's catalog) ───
// Same palette + hash as window.kindColor in index.html so a given kind
// (e.g. "easybitcoin") always renders with the same color across the
// admin Catalog and the investor Home.
const KIND_PALETTE = ['#4f46e5','#0ea5e9','#f59e0b','#ec4899','#10b981','#8b5cf6','#ef4444','#14b8a6','#a855f7','#06b6d4'];
function kindColor(slug) {
  let h = 0;
  for (let i = 0; i < (slug || '').length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return KIND_PALETTE[h % KIND_PALETTE.length];
}
function kindInitial(label) {
  return (label || '?').trim().charAt(0).toUpperCase() || '?';
}

// Source/dest mini-card: kind-colored avatar tile (or brand logo when the
// admin set inv_account_kinds.logo_url) + FROM/TO eyebrow + bold kind
// label + optional institution sub-label. Sets --acct-color so the card
// tint matches the kind even when an image logo overrides the avatar.
const AcctCard = ({ kind, name, institution, kindSlug, logoUrl }) => {
  const color = kindColor(kindSlug);
  return (
    <div className="step-acct-card" style={{ '--acct-color': color }}>
      <div className={'step-acct-avatar' + (logoUrl ? ' has-logo' : '')}>
        {logoUrl
          ? <img src={logoUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.remove('has-logo'); e.currentTarget.parentElement.textContent = kindInitial(name); }} />
          : kindInitial(name)}
      </div>
      <div className="step-acct-text">
        <div className="step-acct-eyebrow">{kind === 'from' ? 'From' : 'To'}</div>
        <div className="step-acct-name">{name}</div>
        {institution && <div className="step-acct-inst">{institution}</div>}
      </div>
    </div>
  );
};

// Down-arrow between source and dest in the vertical step layout. Stroke
// is a gradient from source-kind color → dest-kind color so the eye reads
// the directional handoff.
const StepArrow = ({ gradId, fromKind, toKind }) => {
  const fromColor = kindColor(fromKind);
  const toColor = kindColor(toKind);
  return (
    <div className="step-arrow-fancy" aria-hidden="true">
      <svg viewBox="0 0 40 32" width="40" height="32">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fromColor} />
            <stop offset="100%" stopColor={toColor} />
          </linearGradient>
        </defs>
        <path
          d="M20 4 L20 22 M11 17 L20 26 L29 17"
          stroke={`url(#${gradId})`}
          strokeWidth="2.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

// Pure-visual step card: just the source → dest flow + a row of member
// progress dots. All other per-step stats (Step #, rate, moves YTD,
// value YTD, amount, duration, wait) live in the LoopDetailsPanel that
// slides in when the parent loop card is clicked.
const LoopStep = ({ step, members, focusedMember }) => {
  const isZero = step.rate === 0;
  const executedList = members.map(m => ({ ...m, count: step.executed[m.id] || 0 }));
  const maxExecuted = Math.max(0, ...executedList.map(m => m.count));

  return (
    <div className={'step-node' + (isZero ? ' is-zero' : '')}>
      <div className="step-card">
        <div className="step-flow">
          <AcctCard kind="from" name={step.source} institution={step.sourceInstitution} kindSlug={step.sourceKind} logoUrl={step.sourceLogo} />
          <StepArrow gradId={'step-grad-' + (step.id || step.idx)} fromKind={step.sourceKind} toKind={step.destKind} />
          <AcctCard kind="to" name={step.dest} institution={step.destInstitution} kindSlug={step.destKind} logoUrl={step.destLogo} />
        </div>
        <div className="step-progress">
          {members.map(m => {
            const count = step.executed[m.id] || 0;
            const done = maxExecuted > 0 && count >= maxExecuted;
            const dim = focusedMember && focusedMember !== m.id;
            return (
              <div
                key={m.id}
                className={'step-dot' + (done ? ' done' : ' pending') + (dim ? ' dim' : '')}
                style={{ '--dot-color': m.color }}
                title={`${m.name}: ${count}× executed`}
              >
                <span>{m.initials}</span>
                {done && <svg className="step-check" viewBox="0 0 8 8"><path d="M1.5 4 L3.5 6 L6.5 2" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Loop chain — single account chain showing each unique account once
// with step-info arrows between them. Replaces the previous "row of
// step cards each with redundant source+dest" layout.
//
// Layout: [Acct₁] —step1→ [Acct₂] —step2→ [Acct₃] ... —stepN→ [Acct₁↻]
// Above each account: "Day N" label showing cumulative time elapsed
// when this account becomes the source for the NEXT step.
// Each arrow carries: step number, rate badge, step takes, member
// progress dots (which household members have executed this step).
const LoopChain = ({ loop, members, focusedMember }) => {
  const steps = loop.steps || [];
  if (!steps.length) {
    return <div style={{ padding: 24, color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>This loop has no steps yet.</div>;
  }
  // Build the chain. Node[i] is the account that becomes the source for
  // step i (and the dest of step i-1). Node[N] is the final dest (which
  // closes the loop — typically equals node[0]).
  // holdAfter on each non-first node = wait_duration_days of the step that
  // brought money here (i.e., money sits in this account that long before
  // the next step kicks in).
  //
  // Branches: a position holding the same load on both Taekus cards (Step 1B
  // TK-B, Step 1C TK-C) draws as two source rows — the first on the main
  // line, the other joining it from below — converging on their shared
  // destination. They are alternatives, not consecutive steps.
  const srcNode = (s) => ({
    label: s.source,
    kindSlug: s.sourceKind,
    logo: s.sourceLogo,
    institution: s.sourceInstitution,
    holdAfter: 0,
    holdIsBusiness: false,
  });
  const destNode = (group) => ({
    label: group[0].dest,
    kindSlug: group[0].destKind,
    logo: group[0].destLogo,
    institution: group[0].destInstitution,
    holdAfter: Math.max(...group.map((s) => Number(s.waitDurationDays || 0))),
    holdIsBusiness: group.some((s) => s.waitDurationIsBusiness),
  });
  const positions = loopPositions(steps);
  const startKind = positions[0].steps[0].sourceKind;
  const arrow = (s) => (
    <LoopChainArrow
      step={s}
      members={members}
      focusedMember={focusedMember}
      gradId={`chain-${loop.id || loop.name}-${s.idx}`}
    />
  );

  return (
    <div className="loop-chain">
      {positions.map((p, i) => {
        const end = destNode(p.steps);
        // The loop closes when its last account is the one it started from.
        if (i === positions.length - 1 && end.kindSlug === startKind) end.isReturn = true;
        if (p.steps.length > 1) {
          return (
            <React.Fragment key={p.position}>
              <div className="loop-chain-branch">
                {p.steps.map((s) => (
                  <div key={s.idx} className="loop-chain-branch-row">
                    <LoopChainNode node={srcNode(s)} />
                    <LoopChainArrow
                      step={s}
                      members={members}
                      focusedMember={focusedMember}
                      gradId={`chain-${loop.id || loop.name}-${s.idx}`}
                      noHead
                    />
                  </div>
                ))}
                <div className="loop-chain-branch-join" aria-hidden="true" style={{ '--join-color': kindColor(p.steps[0].destKind) }} />
              </div>
              <div className="loop-chain-branch-tip" aria-hidden="true" style={{ '--join-color': kindColor(p.steps[0].destKind) }}>
                <svg viewBox="0 0 16 16" width="16" height="14"><path d="M0 8 H12 M7 3 L12 8 L7 13" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <LoopChainNode node={end} />
            </React.Fragment>
          );
        }
        const s = p.steps[0];
        // A step whose source is not where the previous step landed is a
        // break in the catalog data, not a hop. Draw it as one — the real
        // source node behind a gap — instead of silently continuing from
        // the last destination (which hid a stray step on Skrill for months).
        const prevDest = i > 0 ? positions[i - 1].steps[0].destKind : null;
        const broken = i > 0 && s.sourceKind && prevDest && s.sourceKind !== prevDest;
        return (
          <React.Fragment key={p.position}>
            {i === 0 && <LoopChainNode node={srcNode(s)} />}
            {broken && (
              <>
                <div className="loop-chain-break" role="img" aria-label={`Chain break: step ${s.label || s.idx} starts from ${s.source}, not ${positions[i - 1].steps[0].dest}`} title={`Step ${s.label || s.idx} starts from ${s.source}, not from ${positions[i - 1].steps[0].dest} where the previous step landed`}>
                  <span>?</span>
                </div>
                <LoopChainNode node={srcNode(s)} />
              </>
            )}
            {arrow(s)}
            <LoopChainNode node={end} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Account node — bigger avatar tile with the account name underneath.
// No enclosing card; the avatar IS the visual anchor. Return-node (when
// the loop closes back to its starting account) gets a subtle ↻ chip.
// "hold for N days" appears below the name when money sits in this
// account between the step that landed it here and the next step.
const LoopChainNode = ({ node }) => {
  const color = kindColor(node.kindSlug);
  const holdLabel = node.holdAfter > 0
    ? `hold for ${node.holdAfter} ${node.holdIsBusiness ? 'biz' : 'cal'} day${node.holdAfter === 1 ? '' : 's'}`
    : null;
  return (
    <div className={'loop-chain-node' + (node.isReturn ? ' is-return' : '')}>
      <div className={'loop-chain-avatar' + (node.logo ? ' has-logo' : '')} style={{ '--acct-color': color }}>
        {node.logo
          ? <img src={node.logo} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.classList.remove('has-logo'); e.currentTarget.parentElement.textContent = kindInitial(node.label); }} />
          : kindInitial(node.label)}
        {node.isReturn && <span className="loop-chain-return-chip" title="Returns to the starting account">↻</span>}
      </div>
      <div className="loop-chain-name">{node.label}</div>
      {node.institution && <div className="loop-chain-inst">{node.institution}</div>}
      {holdLabel && <div className="loop-chain-hold">{holdLabel}</div>}
    </div>
  );
};

// Step arrow between two account nodes. Above the line: Step N + rate
// badge. Below the line: "takes X days" — the step's own duration (how
// long the move itself takes to settle). "Hold" time after settlement
// is shown on the dest node, not on the arrow. Member dots sit at the
// bottom.
const LoopChainArrow = ({ step, members, focusedMember, gradId, noHead }) => {
  const fromColor = kindColor(step.sourceKind);
  const toColor = kindColor(step.destKind);
  const isZero = step.rate === 0;
  const stepDays = Number(step.stepDurationDays || 0);
  const stepIsBusiness = !!step.stepDurationIsBusiness;
  const takesLabel = stepDays === 0
    ? { text: 'Instant', italic: true }
    : { text: `takes ${stepDays} ${stepIsBusiness ? 'biz' : 'cal'} day${stepDays === 1 ? '' : 's'}`, italic: false };
  const executedList = members.map(m => ({ ...m, count: step.executed[m.id] || 0 }));
  const maxExecuted = Math.max(0, ...executedList.map(m => m.count));
  return (
    <div className="loop-chain-arrow">
      <div className="loop-chain-arrow-meta">
        <span className="loop-chain-step-num">Step {step.label || step.idx}</span>
        {!isZero && <span className="loop-chain-step-rate">{fmtPct(step.rate)}</span>}
      </div>
      <div className="loop-chain-arrow-svg" aria-hidden="true">
        <svg viewBox="0 0 64 16" width="100%" height="14" preserveAspectRatio="none">
          <defs>
            {/* A headless branch line is perfectly flat, so its bounding box
                has no height and a bounding-box gradient paints nothing;
                span the gradient in user space instead. */}
            <linearGradient id={gradId} {...(noHead ? { gradientUnits: 'userSpaceOnUse', x1: 0, y1: 0, x2: 64, y2: 0 } : { x1: '0', y1: '0', x2: '1', y2: '0' })}>
              <stop offset="0%" stopColor={fromColor} />
              <stop offset="100%" stopColor={toColor} />
            </linearGradient>
          </defs>
          <path d={noHead ? 'M2 8 H64' : 'M2 8 H54 M48 3 L54 8 L48 13'}
            stroke={`url(#${gradId})`} strokeWidth="2" fill="none"
            strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className={'loop-chain-arrow-foot' + (takesLabel.italic ? ' is-italic' : '')}>{takesLabel.text}</div>
      {members.length > 0 && (
        <div className="loop-chain-arrow-dots">
          {members.map(m => {
            const count = step.executed[m.id] || 0;
            const done = maxExecuted > 0 && count >= maxExecuted;
            const dim = focusedMember && focusedMember !== m.id;
            return (
              <div
                key={m.id}
                className={'step-dot' + (done ? ' done' : ' pending') + (dim ? ' dim' : '')}
                style={{ '--dot-color': m.color }}
                title={`${m.name}: ${count}× executed`}
              >
                <span>{m.initials}</span>
                {done && <svg className="step-check" viewBox="0 0 8 8"><path d="M1.5 4 L3.5 6 L6.5 2" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const LoopConnector = () => (
  <div className="loop-connector">
    <div className="loop-connector-line"></div>
  </div>
);

const LoopReturnArrow = () => (
  <div className="loop-return">
    <svg viewBox="0 0 480 40" preserveAspectRatio="none" width="100%" height="40">
      <path d="M 470 8 Q 470 32 240 32 Q 10 32 10 8" stroke="#c7d2fe" strokeWidth="1.5" fill="none" strokeDasharray="4 4" />
      <path d="M 6 14 L 10 8 L 14 14" stroke="#c7d2fe" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span className="loop-return-label">repeats every cycle</span>
  </div>
);

// ─── Family ───
const NEW_MEMBERS = [
  { id: 'omar',  name: 'Omar',  initials: 'O', role: 'sub', allocation: 3000, valueYTD: 0, moneyFlow: 0, accounts: ['SoFi · queued', 'Coinbase · queued', 'BoA · queued'], color: '#10b981', justAdded: true, addedLabel: 'Added today' },
  { id: 'salma', name: 'Salma', initials: 'S', role: 'sub', allocation: 2000, valueYTD: 0, moneyFlow: 0, accounts: ['SoFi · queued', 'Coinbase · queued', 'BoA · queued'], color: '#8b5cf6', justAdded: true, addedLabel: 'Added today' },
];

const FamilyGrid = ({ members, focused, setFocused, onEdit }) => {
  const holderName = members.find(m => m.role === 'holder')?.name || members[0]?.name || '';
  return (
  <section className="card family-card">
    <header className="card-header">
      <div className="card-title-block">
        <div className="card-eyebrow">Account holder & sub-accounts</div>
        <h2 className="card-title">{holderName}{members.length === 1 ? '' : "'s family"} · {members.length} member{members.length === 1 ? '' : 's'}</h2>
      </div>
      <a className="btn-ghost-pill" href="Add Sub-account.html" style={{textDecoration:'none'}}>+ Add sub-account</a>
    </header>
    <div className="family-grid">
      {members.map(m => (
        <FamilyTile
          key={m.id}
          member={m}
          focused={focused === m.id}
          onClick={() => onEdit && onEdit(m)}
        />
      ))}
    </div>
  </section>
  );
};

// Clicking anywhere on the tile opens the member-edit side panel. The
// focus-filter toggle that used to live on tile click is still
// available via the per-loop member chips (LoopEngine) — this
// preserves the visual highlight when focused via that path.
const FamilyTile = ({ member, focused, onClick }) => {
  const clickable = !member.justAdded;
  return (
    <div
      className={'family-tile' + (focused ? ' is-focused' : '') + (member.justAdded ? ' is-new' : '') + (clickable ? ' is-clickable' : '')}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      aria-label={clickable ? `Edit ${member.name}` : undefined}
      style={{ '--member-color': member.color }}
    >
      {member.justAdded && (
        <span style={{
          position: 'absolute', top: 10, right: 10,
          fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
          padding: '2px 7px', borderRadius: 4,
          background: member.color, color: '#fff',
        }}>New</span>
      )}
      <div className="ft-top">
        <Avatar member={member} size={44} ring={focused} />
        <div className="ft-id">
          <div className="ft-name">{member.name}</div>
          <div className="ft-role">
            {member.role === 'holder'
              ? <span className="ft-pill holder">Account holder</span>
              : <span className="ft-pill sub">Sub-account</span>}
          </div>
        </div>
      </div>
      <div className="ft-stats">
        <div className="ft-stat">
          <div className="ft-stat-lbl">Value · YTD</div>
          <div className="ft-stat-val ft-stat-green">{member.justAdded ? <span style={{color:'var(--text-3)'}}>$0 · pending</span> : fmt$(member.valueYTD)}</div>
        </div>
        <div className="ft-stat">
          <div className="ft-stat-lbl">Money flow</div>
          <div className="ft-stat-val">{member.justAdded ? <span style={{color:'var(--text-3)'}}>—</span> : fmt$Big(member.moneyFlow)}</div>
        </div>
        {/* Per-investor non-loop cost — recurring tools/fees this person
            carries that aren't tied to any specific loop. Edited from the
            "Account costs" modal (writes members.monthly_cost). Without this
            row the figure was computed but never surfaced on the tile. */}
        <div className="ft-stat">
          <div className="ft-stat-lbl">Cost / mo · non-loop</div>
          <div className={'ft-stat-val' + (member.monthlyCost > 0 ? ' ft-stat-red' : '')}>
            {member.monthlyCost > 0 ? '−' + fmt$(Math.round(member.monthlyCost)) : '—'}
          </div>
        </div>
      </div>
      <div className="ft-accounts">
        {member.accounts.map((a, i) => (
          <div key={i} className="ft-acct">
            <span className="ft-acct-dot"></span>
            {a}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Edit Member modal ───
// Opens when the "Edit" pill on a family tile is clicked. Lets the user:
//   • Rename the member (always; persisted to members.name + display_name)
//   • Pick an avatar color
//   • For sub-accounts: deactivate (active=false; preserves history,
//     removes from family grid).
//
// T-043: the per-loop allocation editor is gone with the allocations
// concept (assignment decides who's on a loop — T-017/T-021; costs live in
// the D-15 schedules/items model).
// Avatar color palette — kept in sync with AddSubaccount.jsx. When the user
// hasn't picked anything yet (legacy members pre-2026-05-24), member.color
// will be the deterministic hash output — which may or may not happen to be
// in this list. We still show the swatches; the row simply renders with no
// "selected" outline until the user picks one.
const AVATAR_PALETTE = [
  '#10b981', '#8b5cf6', '#f97316', '#64748b',
  '#4f46e5', '#0ea5e9', '#f59e0b', '#ec4899',
];

const MemberEditModal = ({ member, holderId, subAccounts = [], onClose, navigate }) => {
  const [name, setName] = useState(member.name || '');
  // Initial color from the rendered member (which already prefers
  // members.color over the hash fallback — see useInvestmentData.js).
  const [color, setColor] = useState(member.color || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const isHolder = member.role === 'holder';

  const dispatchReload = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('finops:enrollment-changed', { detail: { memberId: holderId } }));
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');

      // 1. Name + color change (emails are now edited from the
      //    Connectivity hub — see Investment → Connectivity, where holder +
      //    sub-account addresses are managed in one place).
      //
      //    Name and color are batched into a single members UPDATE so we
      //    only round-trip once when either changed.
      const memberPatch = {};
      if (trimmed !== (member.name || '')) {
        memberPatch.display_name = trimmed;
        memberPatch.name = trimmed;
      }
      if (color && color !== (member.color || '')) {
        memberPatch.color = color;
      }
      if (Object.keys(memberPatch).length > 0) {
        const { error: err } = await sb.from('members')
          .update(memberPatch)
          .eq('id', member.id);
        if (err) throw err;
      }

      dispatchReload();
      onClose();
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  const deactivate = async () => {
    setSaving(true);
    setError(null);
    try {
      const sb = getSupabase();
      if (!sb) throw new Error('Supabase client unavailable');
      const { error: err } = await sb.from('members').update({ active: false }).eq('id', member.id);
      if (err) throw err;
      dispatchReload();
      onClose();
    } catch (e) {
      setError(e?.message || String(e));
      setSaving(false);
    }
  };

  const hasChanges = (() => {
    if (name.trim() !== (member.name || '')) return true;
    if (color && color !== (member.color || '')) return true;
    return false;
  })();

  return (
    <div
      onClick={onClose}
      className="member-edit-backdrop"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Edit ${member.name}`}
        className="member-edit-panel"
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
          <Avatar member={member} size={44} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent, #4f46e5)' }}>
              {isHolder ? 'Account holder' : 'Sub-account'}
            </div>
            <h2 style={{ margin: '2px 0 0', fontSize: 19, fontWeight: 800 }}>Edit {member.name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-3, #94a3b8)', padding: 4, marginTop: -2 }}
          >✕</button>
        </div>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3, #94a3b8)', marginBottom: 6 }}>
          Display name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          disabled={saving}
          style={{
            width: '100%', padding: '10px 12px',
            border: '1px solid var(--border-2, #d4d8e6)', borderRadius: 8,
            fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />

        {/* Avatar color picker. The currently rendered bubble color is
            highlighted; clicking any swatch stages the change (persisted
            to members.color on Save). Works for both holder and sub. */}
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3, #94a3b8)', margin: '18px 0 8px' }}>
          Avatar color
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="radiogroup" aria-label="Avatar color">
          {AVATAR_PALETTE.map((p) => {
            const selected = (color || '').toLowerCase() === p.toLowerCase();
            return (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`Set avatar color to ${p}`}
                disabled={saving}
                onClick={() => setColor(p)}
                style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: p,
                  border: selected ? '2px solid var(--text-1, #0f172a)' : '2px solid transparent',
                  boxShadow: selected ? `0 0 0 2px #fff inset, 0 0 0 1px ${p}` : 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  padding: 0,
                  transition: 'transform 120ms ease',
                  transform: selected ? 'scale(1.06)' : 'scale(1)',
                }}
              />
            );
          })}
        </div>

        {/* Email management for the household lives in the dedicated
            Connectivity hub now (Investment → Connectivity). Cleaner
            than stuffing per-row email inputs into this edit panel. */}
        <div className="me-email-pointer">
          <span>
            Email addresses {isHolder ? 'for the household' : 'for this sub-account'} are managed on the{' '}
            <button type="button" className="me-link" onClick={() => { if (typeof navigate === 'function') navigate('email-sources', {}); onClose && onClose(); }}>
              Connectivity page →
            </button>
          </span>
        </div>

        {/* T-043: the per-loop allocation editor is retired — who is on a
            loop is decided by assignment (Kareem), and money math always
            used actuals. */}

        {!isHolder && (
          <div style={{
            marginTop: 18, padding: '14px 16px',
            background: 'var(--rose-bg, #fef2f2)',
            border: '1px dashed var(--rose-border, #fecaca)',
            borderRadius: 10,
          }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--rose, #e11d48)', marginBottom: 6 }}>
              Danger zone
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2, #475569)', marginBottom: 10 }}>
              Deactivating removes <strong>{member.name}</strong> from the family grid. Their history is preserved and they can be reactivated by an admin.
            </div>
            {!confirmDeactivate ? (
              <button
                type="button"
                onClick={() => setConfirmDeactivate(true)}
                disabled={saving}
                style={{ padding: '7px 14px', border: '1px solid var(--rose, #e11d48)', borderRadius: 8, background: 'transparent', color: 'var(--rose, #e11d48)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
              >Deactivate sub-account</button>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--rose, #e11d48)' }}>Sure?</span>
                <button
                  type="button"
                  onClick={deactivate}
                  disabled={saving}
                  style={{ padding: '7px 14px', border: '1px solid var(--rose, #e11d48)', borderRadius: 8, background: 'var(--rose, #e11d48)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                >{saving ? 'Removing…' : 'Yes, deactivate'}</button>
                <button
                  type="button"
                  onClick={() => setConfirmDeactivate(false)}
                  disabled={saving}
                  style={{ padding: '7px 14px', border: 'none', background: 'transparent', color: 'var(--text-2, #475569)', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                >Cancel</button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--rose-bg, #fef2f2)', color: 'var(--rose, #e11d48)', borderRadius: 8, fontSize: 12.5 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving} style={{ padding: '8px 16px' }}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={save} disabled={saving || !name.trim() || !hasChanges} style={{ padding: '8px 16px' }}>
            {saving ? 'Saving…' : hasChanges ? 'Save changes' : 'No changes'}
          </button>
        </div>
      </aside>
    </div>
  );
};

// Small "by admin" attribution pill. Shown on value events whose underlying
// movement was logged by an admin acting on the investor's behalf, so both the
// investor and the admin can tell at a glance who entered it.
const ByAdminPill = () => (
  <span
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      verticalAlign: 'middle',
      fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
      padding: '2px 6px', borderRadius: 999,
      background: 'var(--amber-bg, #fef3c7)', color: 'var(--amber, #b45309)',
      border: '1px solid var(--amber-border, #fcd34d)',
    }}
    title="Entered by an admin acting on your behalf"
  >
    ✎ by admin
  </span>
);

// ─── Activity Feed ───
// Each row is clickable — click to expand an inline drawer with Edit/Delete
// affordances. Edit patches the underlying money_movement (amount/date/memo).
// Delete reverts the value_event (drops it + decrements
// module_enrollment.config.kareem_recovered) and removes the movement —
// mirrors the admin's deleteMovement flow on AdminMemberDetail.
const ActivityFeed = ({ focusedMember, setFocusedMember, holderId, onChanged, isAdmin = false }) => {
  const DATA = useDATA();
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const events = focusedMember ? DATA.recentEvents.filter(e => e.member === focusedMember) : DATA.recentEvents;
  const focusedName = focusedMember ? (DATA.members.find(m => m.id === focusedMember)?.name || focusedMember) : null;

  // Save edits to the underlying money_movement.
  const saveEdit = async (patch) => {
    const sb = getSupabase();
    if (!sb || !editing?.movementId) { alert("Couldn't save — this row isn't tied to a movement we can edit."); setEditing(null); return; }
    const amt = Number(patch.amount);
    if (!Number.isFinite(amt) || amt === 0) { alert('Enter a non-zero amount.'); return; }
    const { error } = await sb.from('money_movements').update({
      amount: amt,
      movement_date: patch.date,
      notes: patch.memo?.trim() || null,
    }).eq('id', editing.movementId);
    if (error) { alert('Save failed: ' + (error.message || error)); return; }
    setEditing(null);
    setOpenId(null);
    if (typeof onChanged === 'function') onChanged();
  };

  // Correction is ONE transactional call now. The old three-step browser
  // sequence (decrement kareem_recovered, delete the value_event, delete the
  // movement) is the exact non-transactional path that produced the July-2026
  // $110 drift, and the M0-tranche-2 guards refuse its pieces anyway.
  // v2_reverse_transfer voids the value, denies the movement, records a typed
  // reversal, and recomputes the hurdle — atomically, with an audit row.
  const performDelete = async () => {
    const sb = getSupabase();
    if (!sb || !deleting) return;
    try {
      const mvId = deleting.movementId;
      if (!mvId) throw new Error('This row has no underlying transfer to reverse.');
      const { error } = await sb.rpc('v2_reverse_transfer', {
        p_movement: mvId, p_reason_code: 'other',
        p_notes: 'removed from V1 Home (was the delete flow)',
      });
      if (error) throw error;
      setDeleting(null);
      setOpenId(null);
      if (typeof onChanged === 'function') onChanged();
    } catch (e) {
      alert('Delete failed: ' + (e?.message || e));
    }
  };

  return (
    <section className="card activity-card">
      <header className="card-header">
        <div className="card-title-block">
          <div className="card-eyebrow">Recent value events</div>
          <h2 className="card-title">
            Activity {focusedMember && <span className="card-title-focus">· {focusedName}</span>}
          </h2>
        </div>
        {focusedMember && (
          <button
            type="button"
            className="btn-ghost-pill"
            onClick={(e) => { e.stopPropagation(); setFocusedMember(null); }}
          >
            Clear filter ×
          </button>
        )}
      </header>
      <div className="activity-list">
        {events.length === 0 && <div className="activity-empty">No value events yet — they'll appear here once the matching engine logs the first movement.</div>}
        {events.map((e, i) => {
          const m = DATA.members.find(x => x.id === e.member) || { name: e.member, initials: '?', color: '#94a3b8' };
          const rowKey = e.id || `idx-${i}`;
          const isOpen = openId === rowKey;
          if (e.settleType) return (
            <div key={rowKey} className={'activity-row activity-row--settle activity-row--settle-' + e.settleType} data-settlement={e.settlementId}>
              <div className="ar-date">{e.date}</div>
              <Avatar member={m} size={26} />
              <div className="ar-flow">
                <div className="ar-flow-line">{e.flow} <span className={'ar-pending ar-settle' + (e.settleType === 'payout' ? ' ar-settle-payout' : '')}>Settlement</span>{e.movementStatus === 'pending' ? <span className="ar-pending">Awaiting Kareem</span> : null}</div>
                <div className="ar-flow-sub">{settlementSub(e, true, m.name)}{e.memo ? <> · {e.memo}</> : null}</div>
              </div>
              <div className="ar-value">
                <div className="ar-value-amt" style={{ color: e.settleType === 'payout' ? 'var(--green, #047857)' : e.settleSign < 0 ? 'var(--rose, #e11d48)' : undefined }}>{settlementAmount(e)}</div>
                <div className="ar-value-split"><span className="split-pill investor">{e.movementStatus === 'pending' ? 'Pending' : e.settleType === 'payout' ? 'Paid to you' : e.settleType === 'payment' ? 'Paid to Kareem' : 'Waived'}</span></div>
              </div>
            </div>
          );
          if (e.poolType) return (
            <PoolFeedRow key={rowKey} e={e} m={m} isOpen={isOpen} isAdmin={isAdmin}
              onToggle={() => setOpenId(isOpen ? null : rowKey)} onChanged={onChanged} />
          );
          return (
            <Fragment key={rowKey}>
              <div
                className={'activity-row is-clickable' + (isOpen ? ' is-open' : '')}
                role="button"
                tabIndex={0}
                onClick={() => setOpenId(isOpen ? null : rowKey)}
                onKeyDown={(evt) => { if (evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); setOpenId(isOpen ? null : rowKey); } }}
                aria-expanded={isOpen}
              >
                <div className="ar-date">{e.date}</div>
                <Avatar member={m} size={26} />
                <div className="ar-flow">
                  <div className="ar-flow-line">{e.flow} {e.enteredByRole === 'admin' && <ByAdminPill />}</div>
                  <div className="ar-flow-sub">{m.name} · {fmt$(e.amount)} moved @ <span className="mono">{fmtPct(e.rate)}</span></div>
                </div>
                <div className="ar-value">
                  <div className={'ar-value-amt' + (e.value === 0 ? ' is-zero' : '')}>{e.value === 0 ? '$0' : '+' + fmt$(e.value)}</div>
                  <div className="ar-value-split">
                    <span className="split-pill kareem">K {fmt$(e.kareem)}</span>
                    <span className="split-pill investor">You {fmt$(e.investor)}</span>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="activity-drawer">
                  <div className="ad-info">
                    <span className="ad-info-lbl">Logged</span>
                    <span className="ad-info-val">{e.eventDate ? new Date(e.eventDate).toLocaleString() : e.date}</span>
                  </div>
                  <div className="ad-info">
                    <span className="ad-info-lbl">Movement</span>
                    <span className="ad-info-val">{e.flow} · {fmt$(e.amount)}</span>
                  </div>
                  <div className="ad-info">
                    <span className="ad-info-lbl">Entered by</span>
                    <span className="ad-info-val">
                      {e.enteredByRole === 'admin'
                        ? <>Admin (on your behalf) <ByAdminPill /></>
                        : e.enteredByRole === 'executor'
                        ? 'You'
                        : '—'}
                    </span>
                  </div>
                  <div className="ad-info">
                    <span className="ad-info-lbl">Value generated</span>
                    <span className="ad-info-val ad-info-val-green">+{fmt$(e.value)}</span>
                  </div>
                  <div className="ad-actions">
                    {e.movementId ? (
                      <>
                        <button type="button" className="btn-ghost" onClick={(evt) => { evt.stopPropagation(); setEditing(e); }} style={{ padding: '6px 12px', fontSize: 12 }}>Edit</button>
                        <button type="button" className="btn-ghost ad-delete-btn" onClick={(evt) => { evt.stopPropagation(); setDeleting(e); }} style={{ padding: '6px 12px', fontSize: 12 }}>Delete</button>
                      </>
                    ) : (
                      <span className="ad-info-lbl">This row isn't linked to a logged movement — contact admin to revise.</span>
                    )}
                  </div>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>

      {editing && (
        <EditActivityModal
          activity={editing}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}

      {deleting && (
        <DeleteActivityConfirm
          activity={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={performDelete}
        />
      )}
    </section>
  );
};

// A pool entry (gold payout, investment payout, carryover) in the feed: it
// moves money into (or out of) the TK-C pool and never generates value, so it
// shows the pool amount instead of a value split. Admins get Edit / Delete,
// which keep the paired gold payment / payout row in step (PoolEntryActions).
function PoolFeedRow({ e, m, isOpen, isAdmin, onToggle, onChanged }) {
  const neg = Number(e.poolAmount) < 0;
  return (
    <Fragment>
      <div
        className={'activity-row is-clickable activity-row--pool' + (isOpen ? ' is-open' : '')}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(evt) => { if (evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); onToggle(); } }}
        aria-expanded={isOpen}
      >
        <div className="ar-date">{e.date}</div>
        <Avatar member={m} size={26} />
        <div className="ar-flow">
          <div className="ar-flow-line">{e.flow} <span className="ar-pending ar-pool">Pool</span></div>
          <div className="ar-flow-sub">{m.name} · {neg ? 'owed back to Kareem from' : 'credited to'} the TK-C pool · no value on this leg</div>
        </div>
        <div className="ar-value">
          <div className="ar-value-amt" style={{ color: neg ? 'var(--rose, #e11d48)' : undefined }}>{neg ? '−' : '+'}{fmt$(e.amount, 2)}</div>
          <div className="ar-value-split"><span className="split-pill investor">Pool</span></div>
        </div>
      </div>
      {isOpen && (
        <div className="activity-drawer">
          <div className="ad-info">
            <span className="ad-info-lbl">Type</span>
            <span className="ad-info-val">{e.srcLabel}</span>
          </div>
          <div className="ad-info">
            <span className="ad-info-lbl">Amount</span>
            <span className="ad-info-val">{neg ? '−' : '+'}{fmt$(e.amount, 2)} {neg ? 'out of' : 'into'} the TK-C pool</span>
          </div>
          {e.note ? (
            <div className="ad-info">
              <span className="ad-info-lbl">Note</span>
              <span className="ad-info-val">{e.note}</span>
            </div>
          ) : null}
          <div className="ad-actions">
            {isAdmin
              ? <PoolEntryActions entry={e} onDone={onChanged} />
              : <span className="ad-info-lbl">Booked by Kareem — ask him to change it.</span>}
          </div>
        </div>
      )}
    </Fragment>
  );
}

// Edit modal for an activity row — patches the underlying money_movement.
// Scoped to amount/date/memo. For confirmed movements only an admin can
// save (the confirmed-movement guard blocks investors); when an admin
// does, the DB-side movement_edit_replay trigger (T-006) replays the
// household ledger — original posted rates preserved, amounts/dates and
// hurdle sequencing recomputed.
function EditActivityModal({ activity, onCancel, onSave }) {
  const [amount, setAmount]   = useState(String(activity.amount || ''));
  const [date, setDate]       = useState((activity.eventDate || '').slice(0, 10));
  const [memo, setMemo]       = useState('');
  const [saving, setSaving]   = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({ amount, date, memo });
    setSaving(false);
  };
  return (
    <div className="settle-modal-backdrop" onClick={onCancel} role="presentation">
      <form className="settle-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="settle-modal-head">
          <div>
            <div className="settle-modal-eyebrow">Activity</div>
            <h3 className="settle-modal-title">Edit movement</h3>
          </div>
          <button type="button" aria-label="Close" className="settle-modal-close" onClick={onCancel}>✕</button>
        </div>
        <label className="settle-modal-field">
          <span>Amount ($)</span>
          <input {...NO_WHEEL} type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={saving} required autoFocus />
        </label>
        <label className="settle-modal-field">
          <span>Movement date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={saving} required />
        </label>
        <label className="settle-modal-field">
          <span>Memo (optional)</span>
          <textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} disabled={saving} placeholder="Notes for your records / for Kareem" />
        </label>
        <div style={{ fontSize: 11.5, color: 'var(--text-3, #94a3b8)' }}>
          If this movement already generated value, saving recomputes the household ledger at the originally posted rates.
          Confirmed movements can only be edited by Kareem (admin).
        </div>
        <div className="settle-modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </form>
    </div>
  );
}

function DeleteActivityConfirm({ activity, onCancel, onConfirm }) {
  const [working, setWorking] = useState(false);
  return (
    <div className="settle-modal-backdrop" onClick={onCancel} role="presentation">
      <div className="settle-modal settle-modal-danger" onClick={(e) => e.stopPropagation()} role="alertdialog">
        <div className="settle-modal-head">
          <div>
            <div className="settle-modal-eyebrow" style={{ color: 'var(--rose, #e11d48)' }}>Delete activity</div>
            <h3 className="settle-modal-title">Are you sure?</h3>
          </div>
          <button type="button" aria-label="Close" className="settle-modal-close" onClick={onCancel}>✕</button>
        </div>
        <p style={{ margin: '0 0 14px', color: 'var(--text-2, #475569)', fontSize: 13.5, lineHeight: 1.5 }}>
          Removing this <strong>{activity.flow} · {fmt$(activity.amount)}</strong> movement also drops the value event it generated
          (<strong className="mono">+{fmt$(activity.value)}</strong>) and reverts the hurdle progress to Kareem by <strong className="mono">{fmt$(activity.kareem)}</strong>.
          This can't be undone — but you can log a fresh movement to replace it.
        </p>
        <div className="settle-modal-actions">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={working}>Cancel</button>
          <button
            type="button"
            className="btn-primary settle-modal-danger-btn"
            disabled={working}
            onClick={async () => { setWorking(true); await onConfirm(); setWorking(false); }}
          >{working ? 'Deleting…' : 'Yes, delete it'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Settlements preview (recent-3 strip on home) ───
const METHOD_TILE = {
  mercury:  { letter: 'M', bg: '#0f172a', fg: '#fff' },
  zelle:    { letter: 'Z', bg: '#6d28d9', fg: '#fff' },
  wire:     { letter: 'W', bg: '#1d4ed8', fg: '#fff' },
  coinbase: { letter: 'C', bg: '#1e40af', fg: '#fff' },
  other:    { letter: '+', bg: '#f3f4fa', fg: '#475569' },
};
const SettlementsPreview = () => {
  const DATA = useDATA();
  const rows = DATA.settlements.recent || [];
  const populated = rows.length > 0;
  // T-039 (D18 discipline): cash figures come from the contract — a confirmed
  // WAIVER is a settlement row but never cash, so summing raw rows here read
  // Mahmoud's $2,605.64 waiver as "confirmed lifetime". lifetimeSettled is the
  // contract's cash-only total (confirmedPayments); pendingTotal likewise.
  const lifetime = DATA.settlements.lifetimeSettled || 0;
  const pending  = DATA.settlements.pendingTotal || 0;
  const outstanding = DATA.settlements.outstanding || 0;
  const credit = DATA.settlements?.credit || 0;
  const raw = DATA.settlements.raw || {};
  const share = equationShare({
    investorNet: raw.investorNet, rewards: raw.totalValue, costs: raw.claimCosts ?? raw.recognizedCosts,
    held: raw.poolHeld, paidIn: raw.paid, paidOut: raw.paidOut, waived: raw.waivers, result: -Number(raw.credit ?? credit),
  });
  return (
    <section className="card settlements-card">
      <header className="card-header">
        <div className="card-title-block">
          <div className="card-eyebrow">Ledger with Kareem</div>
          <h2 className="card-title">Settlements</h2>
          <p className="card-desc">
            {/* D-9: the lifetime figure is directional (what YOU paid in);
                payouts get their own green mention so the two never blur. */}
            {populated
              ? <>{rows.length} recent · {fmt$(lifetime)} confirmed lifetime{(DATA.settlements.paidOutLifetime || 0) > 0 && <> · <span style={{color:'var(--green)',fontWeight:600}}>Kareem paid you {fmt$(DATA.settlements.paidOutLifetime)}</span></>}{pending > 0 && <> · <span style={{color:'var(--amber)',fontWeight:600}}>{fmt$(pending)} pending</span></>}{credit > 0 && <> · <span style={{color:'var(--green)',fontWeight:600}}>Kareem owes you {fmt$(credit)}</span></>}</>
              : credit > 0
                ? <>Kareem owes you <strong style={{color:'var(--green)'}}>{fmt$(credit)}</strong> · rewards {fmtC(DATA.settlements.raw?.totalValue)} − {Number(raw.kareemPaidCosts || 0) > 0 ? 'costs you paid' : 'costs'} {fmtC(DATA.settlements.raw?.claimCosts ?? DATA.settlements.raw?.recognizedCosts)}{share > 0 ? <> − your share {fmtC(share)}</> : null} − Kareem holds {fmtC(DATA.settlements.raw?.poolHeld)}{DATA.settlements.waivers > 0 ? <> − waived {fmtC(DATA.settlements.raw?.waivers)}</> : null}{DATA.settlements.lifetimeSettled > 0 ? <> − paid {fmtC(DATA.settlements.raw?.paid)}</> : null}{(DATA.settlements.raw?.paidOut || 0) > 0 ? <> + paid back {fmtC(DATA.settlements.raw?.paidOut)}</> : null} = −{fmtC(DATA.settlements.raw?.credit ?? credit)}</>
                : <>Outstanding to Kareem: <strong>{fmt$(outstanding)}</strong> · settles {DATA.accountHolder.settlementCadence}, no payments logged yet</>
            }
          </p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <a className="btn-ghost-pill" href="Settlements.html" style={{textDecoration:'none'}}>Full history →</a>
          <a className="btn-primary-sm" href="Settle Now.html" style={{textDecoration:'none',padding:'7px 14px'}}>Settle now</a>
        </div>
      </header>

      {rows.length === 0 ? (
        <div className="settle-empty">
          <div className="settle-empty-art">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M5 8h14M5 12h10M5 16h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="settle-empty-body">
            <div className="settle-empty-title">No settlements yet — {outstanding > 0 ? <>you owe <span className="mono">{fmt$(outstanding)}</span></> : 'nothing outstanding'}</div>
            <div className="settle-empty-sub">Enrolled {DATA.accountHolder.enrolledAtLabel || ''} · first monthly nudge lands {DATA.settlements.nextNudge}. Settle early or wait for the cadence.</div>
          </div>
          {outstanding > 0 && <a className="btn-primary-sm" href="Settle Now.html" style={{textDecoration:'none',padding:'8px 14px',whiteSpace:'nowrap'}}>Settle {fmt$(outstanding)} →</a>}
        </div>
      ) : (
        <div className="settle-rows">
          {rows.map(r => {
            // T-018: methods written by other surfaces (e.g. "Waiver — no
            // cash") aren't in METHOD_TILE — degrade to the generic tile,
            // never crash. The raw method name still renders next to it.
            const tile = METHOD_TILE[r.method] || METHOD_TILE.other;
            // D-9: a payout row is Kareem paying YOU — green, marked, and
            // never confused with your own payments.
            const isPayout = r.direction === 'to_investor';
            return (
              <div key={r.id} className="settle-row">
                <div className="sr-date">
                  <div className="sr-date-main">{r.dateLabel}</div>
                </div>
                <div className="sr-method">
                  <div className="sr-method-tile" style={isPayout ? { background: 'var(--green-bg)', color: 'var(--green)' } : { background: tile.bg, color: tile.fg }}>{isPayout ? '←' : tile.letter}</div>
                  <div>
                    <div className="sr-method-name">{r.methodName}{isPayout && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--green)', background: 'var(--green-bg)', border: '1px solid var(--green-border)', borderRadius: 999, padding: '1px 6px', verticalAlign: 'middle' }}>Kareem → you</span>}</div>
                    <div className="sr-method-ref mono">{r.id.slice(-8)}</div>
                  </div>
                </div>
                <div className="sr-memo">{r.memo || <span className="sr-memo-empty">no memo</span>}</div>
                <div className={'sr-status ' + r.status}>
                  <span className="sr-status-dot"></span>
                  {r.status}
                </div>
                <div className="sr-amount mono" style={isPayout ? { color: 'var(--green)' } : undefined}>{isPayout ? '−' : ''}{fmt$(r.amount)}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ─── Nudges (next actions) ───
// Aggregator that surfaces the next-movement across every subscribed
// loop. Sorts by urgency (overdue first, soonest scheduled next,
// awaiting-kickoff last) so the most pressing thing is at the top.
// Each row carries a "+ Log it" CTA that opens LogMovementModal
// pre-scoped to that loop.
const NextStepsCard = ({ onLogMovement }) => {
  const DATA = useDATA();
  const loops = DATA.loops || [];
  if (!loops.length) return null;
  const rows = loops
    .map((lp) => ({ loop: lp, info: computeNextStep(lp) }))
    .filter((r) => r.info && r.info.step)
    // Drop loops the investor chose to skip for their current position
    // (batching) — they reappear once they actually move (landed_at changes).
    .filter((r) => !r.loop.skipped);
  if (!rows.length) return null;
  // Urgency sort: overdue (most overdue first) → due-today → ready →
  // scheduled (soonest first) → awaiting-kickoff.
  const rank = (s) => ({ overdue: 0, 'due-today': 1, ready: 2, scheduled: 3, 'awaiting-kickoff': 4 }[s] ?? 5);
  rows.sort((a, b) => {
    const dr = rank(a.info.status) - rank(b.info.status);
    if (dr !== 0) return dr;
    const da = a.info.daysRemaining == null ? Number.POSITIVE_INFINITY : a.info.daysRemaining;
    const db = b.info.daysRemaining == null ? Number.POSITIVE_INFINITY : b.info.daysRemaining;
    return da - db;
  });
  const urgentCount = rows.filter((r) => r.info.status === 'overdue' || r.info.status === 'due-today' || r.info.status === 'ready').length;
  return (
    <section className="card next-steps-card">
      <header className="next-steps-header">
        <div>
          <div className="card-eyebrow">Next steps</div>
          <h2 className="card-title">What to do now</h2>
        </div>
        <div className="next-steps-count">
          {urgentCount > 0
            ? <><strong>{urgentCount}</strong> need{urgentCount === 1 ? 's' : ''} attention</>
            : <>{rows.length} upcoming</>}
        </div>
      </header>
      <ul className="next-steps-list">
        {rows.map(({ loop, info }) => (
          <li key={loop.id} className={'next-steps-row next-steps-row--' + info.status}>
            <div className="ns-flow">
              <div className="ns-loop">{loop.name}</div>
              <div className="ns-step">
                <span className="ns-step-num">Step {info.step.label || info.step.idx}</span>
                <span className="ns-step-flow">{info.step.source} → {info.step.dest}</span>
              </div>
            </div>
            <div className={'ns-countdown ns-countdown--' + info.status}>
              {nextStepLabel(info)}
            </div>
            <div className="ns-actions" style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                className="btn-primary-sm ns-log-btn"
                onClick={() => onLogMovement && onLogMovement(loop)}
              >+ Log it</button>
              <button
                type="button"
                className="btn-ghost-sm ns-skip-btn"
                title="Skip this step for now — batching until your next move"
                onClick={() => DATA.skipNextStep && DATA.skipNextStep(loop.id, loop.viewLandedAt)}
              >Skip</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

const NudgesStrip = () => {
  const DATA = useDATA();
  if (!DATA.nudges || DATA.nudges.length === 0) return null;
  return (
  <section className="nudges-strip">
    {DATA.nudges.map((n, i) => (
      <div key={i} className={'nudge ' + n.kind}>
        <div className="nudge-icon">{n.kind === 'pending' ? '⟳' : '✦'}</div>
        <div className="nudge-body">
          <div className="nudge-text">{n.text}</div>
          <div className="nudge-detail">{n.detail}</div>
        </div>
        <button className="nudge-cta">{n.cta}</button>
      </div>
    ))}
  </section>
  );
};

// ─── Account (non-loop) cost editor ──────────────────────────────────────
// Per-member recurring cost NOT tied to any loop (tools, fees a person carries
// regardless of which loop runs). Writes members.monthly_cost; the
// record_member_cost_change DB trigger snapshots history so changing the rate
// today doesn't retro-apply to past months. Folds into settlement via
// useInvestmentData (Kareem absorbs his split-share, same as loop costs).
// Used on BOTH desktop (Home "Account costs" action) and mobile (Add-cost →
// Account cost), so it's a centered modal that also works full-width on phones.
// D-15: AccountCostModal, OneTimeCostModal and the AddCostChooser retired —
// the unified LogCostModal (components/LogCostModal.jsx) is the one entry.

// ─── Mobile Home top block ───────────────────────────────────────────────
// Mobile-only (CSS-gated: `.mobile-home-top` is display:none until the phone
// breakpoint, and scoped under `.inv-home`). Surfaces the four daily investor
// jobs above the fold — what's next (+ log it), where the money is, quick
// log / add-cost, and a demoted hurdle strip — instead of the big hurdle hero.
// Desktop never renders this, so the desktop Home stays pixel-identical.
const MobileHomeTop = ({ member, onLogPreset, onAddCost, onShowDetails, navigate }) => {
  const DATA = useDATA();
  const loops = DATA.loops || [];

  // Most-urgent next step across all loops (same ranking as NextStepsCard).
  const rows = loops
    .map((lp) => ({ loop: lp, info: computeNextStep(lp) }))
    .filter((r) => r.info && r.info.step);
  const rank = (s) => ({ overdue: 0, 'due-today': 1, ready: 2, scheduled: 3, 'awaiting-kickoff': 4 }[s] ?? 5);
  rows.sort((a, b) => {
    const dr = rank(a.info.status) - rank(b.info.status);
    if (dr !== 0) return dr;
    const da = a.info.daysRemaining == null ? Infinity : a.info.daysRemaining;
    const db = b.info.daysRemaining == null ? Infinity : b.info.daysRemaining;
    return da - db;
  });
  const top = rows[0] || null;
  const okStatus = top && top.info.status === 'scheduled';

  // Balances grouped by institution (summed across the family), top 4 + total.
  // Only SET balances count — an unset account's movement-net isn't "holdings".
  const balances = (DATA.balances || []).filter((b) => b.isSet);
  const byInst = {};
  balances.forEach((b) => { byInst[b.institution] = (byInst[b.institution] || 0) + b.balance; });
  const instTop = Object.entries(byInst)
    .map(([inst, amt]) => ({ inst, amt }))
    .sort((a, b) => b.amt - a.amt)
    .slice(0, 4);
  const balTotal = DATA.balanceTotal || 0;

  // Demoted hurdle.
  const hurdle = DATA.accountHolder?.hurdle || 0;
  const recovered = DATA.accountHolder?.recovered || 0;
  const remaining = Math.max(0, hurdle - recovered);
  const pct = hurdle > 0 ? Math.min(100, Math.round((recovered / hurdle) * 100)) : 0;

  const openLog = () => { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('finops:open-log')); };

  return (
    <div className="mobile-home-top">
      {/* T-019: the pool cards ship on phones too — the desktop hero (which
          carries PoolHeroCards) is display:none ≤480px, and every hero-borne
          feature since the 2026-06-08 redesign had silently skipped mobile.
          Same component, self-contained data fetch; the .mht-pool wrapper
          supplies the dark backdrop its hero-stat styling assumes. */}
      <div className="mht-pool">
        <PoolHeroCards member={member} />
      </div>

      {/* NEXT MOVE — the #1 question: what do I do, and when */}
      {top ? (
        <section className={'mht-next mht-next--' + top.info.status}>
          <div className="mht-next-eye">▶ Next move · {top.loop.name}</div>
          <div className="mht-next-flow">{top.info.step.source} → {top.info.step.dest}</div>
          <div className="mht-next-sub">Step {top.info.step.label || top.info.step.idx}{top.info.step.rate ? ` · ${fmtPct(top.info.step.rate)} leg` : ''}</div>
          <span className={'mht-next-due' + (okStatus ? ' ok' : '')}>⏱ {nextStepLabel(top.info)}</span>
          <div className="mht-next-act">
            <button type="button" className="mht-btn-light" onClick={() => onLogPreset && onLogPreset(top.loop)}>Log this move</button>
            <button type="button" className="mht-btn-ghost" onClick={() => onShowDetails && onShowDetails(top.loop)}>See steps</button>
          </div>
        </section>
      ) : (
        <section className="mht-next mht-next--awaiting-kickoff">
          <div className="mht-next-eye">▶ Next move</div>
          <div className="mht-next-flow">Log your first movement</div>
          <div className="mht-next-sub">Tap ➕ Log below to start your loop.</div>
        </section>
      )}

      {/* WITH KAREEM — who owes whom, when Kareem's credit is paid, and
          what you've earned. The desktop hero carries these; it is hidden on
          phones, so they live here too (owner, 2026-10-19). */}
      <MobileKareemCard navigate={navigate} />

      {/* WHERE THE MONEY IS — the #3 question, at a glance */}
      <section className="mht-card">
        <div className="mht-card-head">
          <h3>Where your money is</h3>
          <button type="button" className="mht-link" onClick={() => navigate && navigate('balances')}>Manage →</button>
        </div>
        {instTop.length ? (
          <>
            <div className="mht-bal-grid">
              {instTop.map((r) => (
                <div key={r.inst} className="mht-bal-chip">
                  <div className="mht-bal-inst">{r.inst}</div>
                  <div className="mht-bal-amt">{fmt$(r.amt)}</div>
                </div>
              ))}
            </div>
            <div className="mht-bal-total"><span>Total across accounts</span><span>{fmt$(balTotal)}</span></div>
          </>
        ) : (
          <button type="button" className="mht-bal-empty mht-bal-empty-btn" onClick={() => navigate && navigate('balances')}>
            Set your account balances to see them here →
          </button>
        )}
      </section>

      {/* QUICK ACTIONS — record (#1) + add cost (#4) */}
      <div className="mht-qa">
        <button type="button" className="mht-qa-btn pri" onClick={openLog}>＋ Log movement</button>
        <button type="button" className="mht-qa-btn" onClick={() => onAddCost && onAddCost()}>＋ Add cost</button>
      </div>

      {/* HURDLE — demoted from the hero to a quiet strip */}
      <div className="mht-hurdle">
        <div className="mht-hurdle-ring" style={{ background: `conic-gradient(var(--green,#059669) 0 ${pct}%, #e5e7eb ${pct}% 100%)` }}><i>{pct}%</i></div>
        <div className="mht-hurdle-body">
          <div className="mht-hurdle-t">{remaining > 0 ? `${fmt$(remaining)} to your 50/50 split` : 'Hurdle cleared — 50/50 split'}</div>
          <div className="mht-hurdle-s">{fmt$(recovered)} of {fmt$(hurdle)} hurdle cleared</div>
        </div>
      </div>
    </div>
  );
};

// Phone: the who-owes-whom balance, its payout dates, and all-time net.
const MobileKareemCard = ({ navigate }) => {
  const DATA = useDATA();
  const W = DATA.settlements || {};
  if (W.contract !== true) return null;
  const credit = Number(W.credit || 0);
  const outstanding = Number(W.outstanding || 0);
  const rows = credit > 0 ? scheduleRows(W.schedule, fmt$) : [];
  return (
    <section className="mht-card">
      <div className="mht-card-head">
        <h3>With Kareem</h3>
        <button type="button" className="mht-link" onClick={() => navigate && navigate('settlements')}>History →</button>
      </div>
      <div className={'mht-owe ' + (credit > 0 ? 'credit' : 'owe')}>
        <span>{credit > 0 ? 'Kareem owes you' : 'Outstanding to Kareem'}</span>
        <strong>{fmt$(credit > 0 ? credit : outstanding)}</strong>
      </div>
      {rows.map((r) => (
        <div key={r.label} className={'mht-pay' + (r.overdue ? ' overdue' : '')}>
          <div>
            <div className="mht-pay-t">{r.label}</div>
            <div className="mht-pay-s">{r.sub}</div>
          </div>
          <strong>{fmt$(r.amount)}</strong>
        </div>
      ))}
      <div className="mht-bal-total"><span>Net earned · all time</span><span>{fmt$(Number(W.investorNet || 0))}</span></div>
    </section>
  );
};

// ─── App root ───
function Home({ profile, member, navigate, actingAsAdmin = false } = {}) {
  const tweaks = TWEAK_DEFAULTS;
  // Loop details panel + scoped log-movement modal state. detailsLoop is
  // the loop object whose details to show in the side panel; logMovementLoop
  // pre-scopes the LogMovementModal to a specific loop's steps when set.
  const [detailsLoop, setDetailsLoop] = useState(null);
  const [logMovementLoop, setLogMovementLoop] = useState(null);
  // D-15: ONE cost entry point (owner design 2026-08-22) — the unified
  // Log-a-cost modal replaces the chooser, the account-cost editor, and the
  // one-time modal. Legacy monthly rates retire with Session C.
  const [addCostOpen, setAddCostOpen] = useState(false);
  const [focusedMember, setFocusedMember] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const isAdmin = profile?.role === 'admin';

  // Real data from Supabase, shaped to match the prototype's mock object.
  // Admin gets a virtual default enrollment so they see the screen even
  // without their own enrollment row.
  const { status, data, error, reload, skipNextStep, unskipNextStep } = useInvestmentData(member, profile);
  // appState branches the existing render paths (subscribed / no-loop / no-events).
  const appState =
    status === 'subscribed'         ? 'subscribed' :
    status === 'enrolled-no-events' ? 'enrolled-no-events' :
    status === 'enrolled-no-loop'   ? 'enrolled-no-loop' :
    'subscribed';

  // Open the in-app catalog admin screen. (We used to bounce out to the
  // parent dashboard's Settings → Investment Catalog tab — moving it
  // inside the investment module per the 2026-05-22 redesign.)
  const openCatalogAdmin = () => {
    if (typeof navigate === 'function') navigate('catalog-admin', {});
  };

  // ── Pre-render states ────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="app density-comfortable" style={{ padding: 32, color: '#6b7280' }}>
        Loading your investment data…
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="app density-comfortable" style={{ padding: 32 }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#9f1239', padding: 16, borderRadius: 12, maxWidth: 640 }}>
          <strong>Couldn't load investment data.</strong>
          <p style={{ fontFamily: 'monospace', fontSize: 12, margin: '8px 0 0' }}>{String(error?.message || error)}</p>
        </div>
      </div>
    );
  }
  if (status === 'no-enrollment') {
    return (
      <div className="app density-comfortable" style={{ padding: 32 }}>
        {isAdmin && !actingAsAdmin && <AdminBanner onOpen={openCatalogAdmin} text="You can manage the catalog even without personal enrollment." block />}
        <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-soft)', color: 'var(--accent-2)', padding: 16, borderRadius: 'var(--radius-lg)', maxWidth: 640 }}>
          <strong>The investment module isn't enabled for your account.</strong>
          <p style={{ margin: '8px 0 0' }}>Ask Kareem to enroll you (insert a row into <code className="mono">module_enrollment</code> with <code className="mono">module_name='investment'</code>).</p>
        </div>
      </div>
    );
  }

  // ── Happy paths: wrap in DataCtx.Provider so helpers can pull DATA ───
  // Inject the skip actions onto the context value so the next-step card helpers
  // (which read useDATA()) can call them.
  const DATA = data ? { ...data, skipNextStep, unskipNextStep } : data;
  return (
   <DataCtx.Provider value={DATA}>
    <div className={'app inv-home density-' + tweaks.density.toLowerCase()}>
      {isAdmin && !actingAsAdmin && <AdminBanner onOpen={openCatalogAdmin} text="You can manage steps & loops in the catalog." />}

      <main className="page">
        <div className="page-title-row">
          <div>
            <div className="page-eyebrow">Investment module · Account holder view</div>
            <h1 className="page-title">Investment — {DATA.accountHolder.name}{DATA.members.length > 1 ? "'s family" : ''}</h1>
            <p className="page-sub">
              {appState === 'enrolled-no-loop'
                ? <>Enrolled <strong>{DATA.accountHolder.enrolledAtLabel}</strong> · Hurdle <strong>${DATA.accountHolder.hurdle.toLocaleString()}</strong> · No loops subscribed yet</>
                : appState === 'enrolled-no-events'
                ? <>Enrolled <strong>{DATA.accountHolder.enrolledAtLabel}</strong> · Subscribed <strong>{DATA.loop?.name || ''}</strong> · Awaiting first execution</>
                : <>Enrolled {DATA.accountHolder.enrolledAtLabel} · Hurdle <strong>${DATA.accountHolder.hurdle.toLocaleString()}</strong> · Split after hurdle <strong>{Math.round((DATA.accountHolder.splitAfterHurdle || 0.5) * 100)}/{Math.round((1 - (DATA.accountHolder.splitAfterHurdle || 0.5)) * 100)}</strong> · Settles <strong>{DATA.accountHolder.settlementCadence}</strong></>
              }
            </p>
          </div>
          <div className="page-actions">
            {/* T-017: the "Browse loop catalog" CTAs are gone — loops are
                assigned by Kareem and subscriptions activate on assignment,
                so there is nothing for an investor to browse. */}
            {appState === 'enrolled-no-loop' ? (
              <>
                <button className="btn-ghost" onClick={() => setHowOpen(true)}>How it works</button>
                <a className="btn-ghost" href="Email Sources.html" style={{textDecoration:'none',display:'inline-block'}}>Connectivity</a>
                <button className="btn-ghost" onClick={() => setLogMovementLoop(DATA.loops?.[0] || null)}>+ Log movement</button>
              </>
            ) : appState === 'enrolled-no-events' ? (
              <>
                {/* No top-level "+ Log movement" here — the loop card below
                    carries its own per-loop button so the action is
                    scoped to a specific loop, which is what the matching
                    engine needs anyway. */}
                <a className="btn-ghost" href="Email Sources.html" style={{textDecoration:'none',display:'inline-block'}}>Connectivity</a>
              </>
            ) : (
              <>
                {/* "+ Log movement" lives on each loop card now (LoopEngine's
                    header action), so we don't duplicate it up here. */}
                <button className="btn-ghost" onClick={() => setAddCostOpen(true)}>＋ Add cost</button>
                <a className="btn-ghost" href="Settle Now.html" style={{textDecoration:'none',display:'inline-block'}}>Settle now</a>
                <a className="btn-ghost" href="Email Sources.html" style={{textDecoration:'none',display:'inline-block'}}>Connectivity</a>
              </>
            )}
          </div>
        </div>

        {appState === 'enrolled-no-loop' ? (
          <>
            <TeaserHero />
            <HowItWorks />
            <TermsCard />
            {/* T-009 Build 1: a household with a mapped TK-C card can have
                pending draws before any loop/event exists — the review card
                must reach them in every Home state, not just the rich one. */}
            <PendingDrafts member={member} profile={profile} />
            <CostSuggestionsCard member={member} members={DATA.members} />
            <FamilyGrid members={DATA.members} focused={focusedMember} setFocused={setFocusedMember} onEdit={setEditingMember} />
          </>
        ) : appState === 'enrolled-no-events' && DATA.loop ? (
          <>
            <EmptyHurdleHero />
            <NoEventsLoopBanner />
            <PendingDrafts member={member} profile={profile} />
            <CostSuggestionsCard member={member} members={DATA.members} />
            {(DATA.loops || []).map((lp) => (
              <LoopEngine
                key={lp.id}
                loop={{
                  ...lp,
                  valueYTD: 0,
                  cyclesCompleted: 0,
                  cyclesInProgress: 0,
                  steps: (lp.steps || []).map(s => ({
                    ...s,
                    movesYTD: 0,
                    valueYTD: 0,
                    executed: Object.fromEntries(DATA.members.map(m => [m.id, 0])),
                  })),
                }}
                members={DATA.members.map(m => ({ ...m, valueYTD: 0, moneyFlow: 0 }))}
                focusedMember={focusedMember}
                setFocusedMember={setFocusedMember}
                onLogMovement={setLogMovementLoop}
                onShowDetails={setDetailsLoop}
              />
            ))}
            <FamilyGrid members={DATA.members.map(m => ({ ...m, valueYTD: 0, moneyFlow: 0 }))} focused={focusedMember} setFocused={setFocusedMember} onEdit={setEditingMember} />
            <NoEventsCard />
          </>
        ) : (
          <>
            <MobileHomeTop
              member={member}
              onLogPreset={setLogMovementLoop}
              onAddCost={() => setAddCostOpen(true)}
              onShowDetails={setDetailsLoop}
              navigate={navigate}
            />
            <HurdleHero tweaks={tweaks} member={member} adminView={isAdmin && !actingAsAdmin} />
            <PendingDrafts member={member} profile={profile} />
            <CostSuggestionsCard member={member} members={DATA.members} />
            <NextStepsCard onLogMovement={setLogMovementLoop} />
            <NudgesStrip />
            {(DATA.loops || []).map((lp) => (
              <LoopEngine
                key={lp.id}
                loop={lp}
                members={DATA.members}
                focusedMember={focusedMember}
                setFocusedMember={setFocusedMember}
                onLogMovement={setLogMovementLoop}
                onShowDetails={setDetailsLoop}
              />
            ))}
            <FamilyGrid members={DATA.members} focused={focusedMember} setFocused={setFocusedMember} onEdit={setEditingMember} />
            <SettlementsPreview />
            <ActivityFeed focusedMember={focusedMember} setFocusedMember={setFocusedMember} holderId={member?.id} onChanged={reload} isAdmin={isAdmin} />
          </>
        )}

        <footer className="page-foot">
          Value math: <span className="mono">amount × step.rate</span> per movement · append-only ledger · partnership math always uses <strong>actual</strong> movements
        </footer>
      </main>
      {howOpen && <HowItWorksModal onClose={() => setHowOpen(false)} navigate={navigate} />}
      {logMovementLoop && <LogMovementModal member={member} profile={profile} presetLoop={logMovementLoop} onClose={() => setLogMovementLoop(null)} />}
      {addCostOpen && (
        <LogCostModal
          mode="investor"
          members={DATA.members}
          onClose={() => setAddCostOpen(false)}
          onSaved={() => { setAddCostOpen(false); if (typeof reload === 'function') reload(); }}
        />
      )}
      {detailsLoop && (
        <LoopDetailsPanel
          loop={detailsLoop}
          members={DATA.members}
          isAdmin={isAdmin}
          navigate={navigate}
          onLoopUpdated={() => { setDetailsLoop(null); if (typeof reload === 'function') reload(); }}
          onClose={() => setDetailsLoop(null)}
        />
      )}
      {editingMember && (
        <MemberEditModal
          member={editingMember}
          holderId={member?.id}
          subAccounts={DATA.members.filter((m) => m.role !== 'holder')}
          navigate={navigate}
          onClose={() => setEditingMember(null)}
        />
      )}
    </div>
   </DataCtx.Provider>
  );
}

export default Home;

// Named exports so the admin overview can reuse the same loop side-panel
// + hero-logo + facts UI without duplicating the implementation.
export {
  LoopDetailsPanel,
  LoopHeroLogo,
  LoopFactsStrip,
  PaymentMethodIcon,
  PAYMENT_METHODS,
  collectLoopFacts,
  kindColor,
  kindInitial,
};
